import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import './ReceptionistDashboard.css';

function AddStudentReceptionist({ studentToEdit, onCancel }) {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isEditMode = !!studentToEdit;
  
  // Add Student form state
  const [studentForm, setStudentForm] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    ageYears: '',
    gender: '',
    nationality: 'Kenyan',
    phone: '',
    classId: '',
    sectionId: '',
    admissionDate: '',
    previousSchool: '',
    previousSchoolGrade: '', // Grade or percentage from previous school
    studentIdNo: '',
    parents: [
      {
        name: '',
        relationship: '', // Mother | Father | Guardian
        primaryContact: 'primary', // 'primary' | 'secondary'
        email: '',
        password: '',
        confirmPassword: '',
        phone: '',
        address: '',
        canPickup: false,
        isEmergencyContact: false,
        paysFees: false,
        useExisting: false,
        existingParentId: null,
        documents: [] // Array of {type: '', file: File, url: ''} for this parent
      }
    ],
    address: '',
    useParentAddress: false, // Option to use parent address as student address
    selectedParentAddressIndex: '', // Index of selected parent address
    emergencyContact: '',
    emergencyPhone: '',
    profilePicture: null,
    // Medical Details
    medicalDetails: '',
    emergencyDocuments: null,
    // Transport Service
    transportServiceEnabled: false,
    pickupLocationId: '',
    useStudentAddressAsPickup: false, // Option to use student address as pickup location
    transportRouteType: '', // '1 Way' or '2 Way'
    transportTime: '', // 'Morning' or 'Evening' (for 1 Way routes)
    routeId: '', // Selected route ID
    // Fees
    paymentMethod: '',
    // Documents
    documents: [] // Array of {type: '', file: File, url: ''}
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Sync parent address when student address changes if useParentAddress is checked
  useEffect(() => {
    if (studentForm.useParentAddress && studentForm.selectedParentAddressIndex && studentForm.address) {
      const parentIndex = parseInt(studentForm.selectedParentAddressIndex);
      setStudentForm(prev => {
        const updatedParents = [...prev.parents];
        if (updatedParents[parentIndex]) {
          updatedParents[parentIndex] = {
            ...updatedParents[parentIndex],
            address: prev.address
          };
        }
        return {
          ...prev,
          parents: updatedParents
        };
      });
    }
  }, [studentForm.address, studentForm.useParentAddress, studentForm.selectedParentAddressIndex]);
  const [submissionError, setSubmissionError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showParentPasswords, setShowParentPasswords] = useState([false]);
  const [showParentConfirmPasswords, setShowParentConfirmPasswords] = useState([false]);
  
  // Country code states - Default to Kenya (+254)
  const [receptionistStudentPhoneCountryCode, setReceptionistStudentPhoneCountryCode] = useState('+254');
  const [receptionistParentPhoneCountryCodes, setReceptionistParentPhoneCountryCodes] = useState(['+254']);
  const [receptionistEmergencyPhoneCountryCode, setReceptionistEmergencyPhoneCountryCode] = useState('+254');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [addedStudentName, setAddedStudentName] = useState('');
  const [classes, setClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [formBranchId, setFormBranchId] = useState(null); // Branch selected in form
  
  // Parent selection state
  const [existingParents, setExistingParents] = useState([]);
  const [loadingParents, setLoadingParents] = useState(false);
  const [parentSearchTerms, setParentSearchTerms] = useState(['']); // Array of search terms for each parent
  
  // Siblings state
  const [siblings, setSiblings] = useState([]);
  const [loadingSiblings, setLoadingSiblings] = useState(false);
  
  // Teachers state
  const [teachers, setTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  
  // Fees state
  const [classFees, setClassFees] = useState(null);
  const [additionalFees, setAdditionalFees] = useState([]);
  const [loadingFees, setLoadingFees] = useState(false);
  
  // Pickup locations state
  const [pickupLocations, setPickupLocations] = useState([]);
  const [loadingPickupLocations, setLoadingPickupLocations] = useState(false);
  
  // Helper function to get currency symbol
  const getCurrencySymbol = (currency) => {
    const currencySymbols = {
      'USD': '$',
      'PKR': '₨',
      'EUR': '€',
      'GBP': '£',
      'INR': '₹',
      'AED': 'د.إ',
      'SAR': '﷼',
      'CAD': 'C$',
      'AUD': 'A$',
      'JPY': '¥',
      'CNY': '¥'
    };
    return currencySymbols[currency] || currency || '$';
  };
  
  // Get selected pickup location details
  const selectedPickupLocation = pickupLocations.find(
    loc => loc.id.toString() === studentForm.pickupLocationId?.toString()
  );
  

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (studentToEdit && schoolData) {
      // Format dates for input fields (YYYY-MM-DD)
      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      };

      setStudentForm({
        firstName: studentToEdit.first_name || '',
        middleName: studentToEdit.middle_name || '',
        lastName: studentToEdit.last_name || '',
        email: studentToEdit.email || '',
        password: '',
        confirmPassword: '',
        dateOfBirth: formatDateForInput(studentToEdit.date_of_birth),
        ageYears: '',
        gender: studentToEdit.gender || '',
        nationality: studentToEdit.nationality || '',
        phone: studentToEdit.phone || '',
        classId: studentToEdit.class_id || '',
        sectionId: studentToEdit.section_id || '',
        admissionDate: formatDateForInput(studentToEdit.admission_date),
        previousSchool: studentToEdit.previous_school || '',
        previousSchoolGrade: studentToEdit.previous_school_grade || '',
        studentIdNo: studentToEdit.student_id_no || '',
        parents: studentToEdit.parent_name ? [
          {
            name: studentToEdit.parent_name || '',
            relationship: studentToEdit.parent_relationship || '',
            primaryContact: studentToEdit.parent_is_primary ? 'primary' : 'secondary',
            email: studentToEdit.parent_email || '',
            password: '',
            confirmPassword: '',
            phone: studentToEdit.parent_phone || '',
            address: studentToEdit.parent_address || studentToEdit.address || '',
            canPickup: studentToEdit.parent_can_pickup || false,
            isEmergencyContact: studentToEdit.parent_is_emergency_contact || false,
            paysFees: studentToEdit.parent_pays_fees || false,
            useExisting: false,
            existingParentId: null,
            documents: []
          }
        ] : [
          {
            name: '',
            relationship: '',
            primaryContact: 'primary',
            email: '',
            password: '',
            confirmPassword: '',
            phone: '',
            address: '',
            canPickup: false,
            isEmergencyContact: false,
            paysFees: false,
            useExisting: false,
            existingParentId: null,
            documents: []
          }
        ],
        address: studentToEdit.address || '',
        emergencyContact: studentToEdit.emergency_contact || '',
        emergencyPhone: studentToEdit.emergency_phone || '',
        profilePicture: null,
        medicalDetails: studentToEdit.medical_details || '',
        emergencyDocuments: null,
        transportServiceEnabled: studentToEdit.transport_service_enabled || false,
        pickupLocationId: studentToEdit.pickup_location_id || '',
        useStudentAddressAsPickup: false, // Reset for edit mode
        transportRouteType: studentToEdit.transport_route_type || '',
        transportTime: studentToEdit.transport_time || '',
        routeId: studentToEdit.route_id || '',
        paymentMethod: studentToEdit.payment_method || '',
        documents: []
      });

      // Initialize parent arrays for edit mode
      const parentCount = studentToEdit.parent_name ? 1 : 1; // Start with at least one parent
      setReceptionistParentPhoneCountryCodes(Array(parentCount).fill(''));
      setShowParentPasswords(Array(parentCount).fill(false));
      setShowParentConfirmPasswords(Array(parentCount).fill(false));
      setParentSearchTerms(Array(parentCount).fill(''));

      // Fetch sections for the class
      if (studentToEdit.class_id) {
        fetchSectionsForClass(studentToEdit.class_id);
      }
    }
  }, [studentToEdit, schoolData]);

  useEffect(() => {
    // Fetch classes for the school, filtered by branch if selected
    const fetchClasses = async () => {
      if (!schoolData?.id) return;
      
      try {
        setLoadingClasses(true);
        let query = supabase
          .from('classes')
          .select('*')
          .eq('school_id', schoolData.id)
          .eq('is_active', true);
        
        // Filter by branch if one is selected
        if (formBranchId) {
          query = query.eq('branch_id', formBranchId);
        }
        
        const { data, error } = await query.order('class_name');
        
        if (error) {
          console.error('Error fetching classes:', error);
          return;
        }
        
        setClasses(data || []);
        
        // Reset selected class and section if they're no longer in the filtered list
        if (studentForm.classId) {
          const classExists = data?.some(c => c.id.toString() === studentForm.classId.toString());
          if (!classExists) {
            setStudentForm(prev => ({
              ...prev,
              classId: '',
              sectionId: ''
            }));
          }
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoadingClasses(false);
      }
    };

    if (schoolData?.id) {
      fetchClasses();
    }
  }, [schoolData?.id, formBranchId]);

  // Fetch branches when school data is available
  useEffect(() => {
    const fetchBranches = async () => {
      if (!schoolData?.id) return;
      
      try {
        setLoadingBranches(true);
        const { data, error } = await supabase
          .from('branches')
          .select('*')
          .eq('school_id', schoolData.id)
          .eq('is_active', true)
          .order('branch_name');
        
        if (error) {
          console.error('Error fetching branches:', error);
          setBranches([]);
        } else {
          setBranches(data || []);
        }
      } catch (err) {
        console.error('Error:', err);
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    if (schoolData?.id) {
      fetchBranches();
    }
  }, [schoolData?.id]);

  // Reset class and section selection when branch changes
  useEffect(() => {
    if (formBranchId !== null) {
      setStudentForm(prev => ({
        ...prev,
        classId: '',
        sectionId: ''
      }));
    }
  }, [formBranchId]);

  useEffect(() => {
    // Fetch pickup locations for the school with route and vehicle information
    const fetchPickupLocations = async () => {
      if (!schoolData?.id) return;
      
      try {
        setLoadingPickupLocations(true);
        const { data, error } = await supabase
          .from('pickup_locations')
          .select('id, location_name, address, price_one_way, price_two_way, currency')
          .eq('school_id', schoolData.id)
          .eq('is_active', true)
          .order('location_name', { ascending: true });
        
        if (error) {
          console.error('Error fetching pickup locations:', error);
          return;
        }
        
        // For each pickup location, fetch routes and vehicles
        const locationsWithRoutes = await Promise.all(
          (data || []).map(async (location) => {
            // Fetch routes that use this pickup location
            const { data: routeLocationData } = await supabase
              .from('route_pickup_locations')
              .select('route_id')
              .eq('pickup_location_id', location.id);
            
            let routes = [];
            if (routeLocationData && routeLocationData.length > 0) {
              const routeIds = routeLocationData.map(rl => rl.route_id);
              
              // Fetch route details
              const { data: routesData } = await supabase
                .from('routes')
                .select('id, route_type, route_name, vehicle_id')
                .in('id', routeIds)
                .eq('is_active', true);
              
              if (routesData && routesData.length > 0) {
                // Fetch vehicles for each route
                const routesWithVehicles = await Promise.all(
                  routesData.map(async (route) => {
                    const { data: vehicleData } = await supabase
                      .from('transport_vehicles')
                      .select('id, bus_number, driver_name')
                      .eq('id', route.vehicle_id)
                      .single();
                    
                    return {
                      id: route.id,
                      route_type: route.route_type,
                      route_name: route.route_name,
                      vehicle: vehicleData || null
                    };
                  })
                );
                
                routes = routesWithVehicles;
              }
            }
            
            return {
              ...location,
              routes: routes
            };
          })
        );
        
        setPickupLocations(locationsWithRoutes);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoadingPickupLocations(false);
      }
    };
    
    if (schoolData?.id) {
      fetchPickupLocations();
    }
  }, [schoolData?.id]);

  // Auto-select route when pickup location or route type changes
  useEffect(() => {
    if (studentForm.pickupLocationId && pickupLocations.length > 0) {
      const selectedLocation = pickupLocations.find(loc => loc.id === studentForm.pickupLocationId);
      if (selectedLocation && selectedLocation.routes && selectedLocation.routes.length > 0) {
        let routeToSelect = null;
        
        // If route type is selected, find matching route
        if (studentForm.transportRouteType) {
          const matchingRoutes = selectedLocation.routes.filter(route => {
            if (studentForm.transportRouteType === '1 Way') {
              return route.route_type === '1 Way Morning' || route.route_type === '1 Way Evening';
            } else if (studentForm.transportRouteType === '2 Way') {
              return route.route_type === '2 Way';
            }
            return false;
          });
          if (matchingRoutes.length > 0) {
            routeToSelect = matchingRoutes[0].id.toString();
          }
        } else {
          // If no route type selected, select the first route
          routeToSelect = selectedLocation.routes[0].id.toString();
        }
        
        // Only update if route is different from current selection
        if (routeToSelect && studentForm.routeId !== routeToSelect) {
          setStudentForm(prev => ({
            ...prev,
            routeId: routeToSelect
          }));
        }
      } else if (!selectedLocation || !selectedLocation.routes || selectedLocation.routes.length === 0) {
        // Clear route if no routes available
        if (studentForm.routeId) {
          setStudentForm(prev => ({
            ...prev,
            routeId: ''
          }));
        }
      }
    } else if (!studentForm.pickupLocationId && studentForm.routeId) {
      // Clear route if pickup location is cleared
      setStudentForm(prev => ({
        ...prev,
        routeId: ''
      }));
    }
  }, [studentForm.pickupLocationId, studentForm.transportRouteType, pickupLocations]);

  const handleLogout = () => {
    navigate('/receptionistlogin');
  };

  // Add Student form functions
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setStudentForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Debug logging for class selection
    if (name === 'classId') {
      console.log('Class selected:', value);
      console.log('Available classes:', classes);
      const selectedClass = classes.find(c => c.id === value);
      console.log('Selected class details:', selectedClass);
    }
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // If class is changed, fetch sections for that class
    if (name === 'classId') {
      if (value) {
        fetchSectionsForClass(value);
      } else {
        setSections([]);
        setStudentForm(prev => ({
          ...prev,
          sectionId: ''
        }));
      }
      // Clear teachers when class changes
      setTeachers([]);
      // Fetch fees when class changes
      if (value) {
        fetchClassFees(value);
      } else {
        setClassFees(null);
      }
    }
    
    // If class or section is changed, fetch teachers
    if (name === 'classId' || name === 'sectionId') {
      const classId = name === 'classId' ? value : studentForm.classId;
      const sectionId = name === 'sectionId' ? value : studentForm.sectionId;
      if (classId && sectionId) {
        fetchTeachersForClassSection(classId, sectionId);
      } else {
        setTeachers([]);
      }
    }
    
    // Auto-calc age when DOB changes
    if (name === 'dateOfBirth') {
      if (value) {
        try {
          const dob = new Date(value);
          const today = new Date();
          let age = today.getFullYear() - dob.getFullYear();
          const m = today.getMonth() - dob.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
            age--;
          }
          setStudentForm(prev => ({ ...prev, ageYears: age.toString() }));
          
          // Validate age limits
          if (age > 100) {
            setFormErrors(prev => ({
              ...prev,
              dateOfBirth: 'Student age cannot exceed 100 years'
            }));
          } else if (age < 2) {
            setFormErrors(prev => ({
              ...prev,
              dateOfBirth: 'Student must be at least 2 years old'
            }));
          } else {
            // Clear error if age is valid
            setFormErrors(prev => {
              const newErrors = { ...prev };
              if (newErrors.dateOfBirth === 'Student age cannot exceed 100 years' || 
                  newErrors.dateOfBirth === 'Student must be at least 2 years old') {
                delete newErrors.dateOfBirth;
              }
              return newErrors;
            });
          }
        } catch (err) {
          setStudentForm(prev => ({ ...prev, ageYears: '' }));
        }
      } else {
        setStudentForm(prev => ({ ...prev, ageYears: '' }));
      }
    }
    
  };

  // Helper function to handle parent input changes
  const handleParentInputChange = (parentIndex, field, value) => {
    setStudentForm(prev => {
      const newParents = [...prev.parents];
      newParents[parentIndex] = {
        ...newParents[parentIndex],
        [field]: value
      };
      
      const updatedForm = {
        ...prev,
        parents: newParents
      };
      
      // If address is changed and we're using this parent's address, update student address
      if (field === 'address' && prev.useParentAddress && prev.selectedParentAddressIndex === parentIndex.toString()) {
        updatedForm.address = value;
      }
      
      return updatedForm;
    });

    // Clear errors for this field
    const errorKey = `parents.${parentIndex}.${field}`;
    if (formErrors[errorKey]) {
      setFormErrors(prev => ({
        ...prev,
        [errorKey]: ''
      }));
    }

    // If parent email is changed, fetch siblings
    if (field === 'email' && value.trim()) {
      fetchSiblings(value.trim());
    } else if (field === 'email' && !value.trim()) {
      setSiblings([]);
    }
  };

  // Helper function to add a new parent
  const addParent = () => {
    setStudentForm(prev => ({
      ...prev,
      parents: [
        ...prev.parents,
        {
          name: '',
          relationship: '',
          primaryContact: 'primary',
          email: '',
          password: '',
          confirmPassword: '',
          phone: '',
          address: '',
          canPickup: false,
          isEmergencyContact: false,
          paysFees: false,
          useExisting: false,
          existingParentId: null,
          documents: []
        }
      ]
    }));
    setReceptionistParentPhoneCountryCodes(prev => [...prev, '+254']);
    setShowParentPasswords(prev => [...prev, false]);
    setShowParentConfirmPasswords(prev => [...prev, false]);
    setParentSearchTerms(prev => [...prev, '']);
  };

  // Helper function to remove a parent
  const removeParent = (index) => {
    if (studentForm.parents.length > 1) {
      setStudentForm(prev => ({
        ...prev,
        parents: prev.parents.filter((_, i) => i !== index)
      }));
      setReceptionistParentPhoneCountryCodes(prev => prev.filter((_, i) => i !== index));
      setShowParentPasswords(prev => prev.filter((_, i) => i !== index));
      setShowParentConfirmPasswords(prev => prev.filter((_, i) => i !== index));
      setParentSearchTerms(prev => prev.filter((_, i) => i !== index));
      
      // Clear errors for removed parent
      setFormErrors(prev => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach(key => {
          if (key.startsWith(`parents.${index}.`)) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
    }
  };

  const fetchSectionsForClass = async (classId) => {
    try {
      setLoadingSections(true);
      const { data, error } = await supabase
        .from('classes')
        .select('sections')
        .eq('id', classId)
        .single();
      
      if (error) {
        console.error('Error fetching sections:', error);
        setSections([]);
        return;
      }
      
      if (data && data.sections && Array.isArray(data.sections)) {
        setSections(data.sections.filter(section => section.trim() !== ''));
      } else {
        setSections([]);
      }
    } catch (err) {
      console.error('Error:', err);
      setSections([]);
    } finally {
      setLoadingSections(false);
    }
  };

  const fetchExistingParents = async (searchTerm = '', parentIndex = 0) => {
    if (!schoolData?.id) return;

    setLoadingParents(true);
    try {
      // First, get all parent emails/IDs that have at least one active student
      const { data: activeStudentsData } = await supabase
        .from('students')
        .select('parent_id, parent_email')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .not('parent_email', 'is', null);
      
      // Extract unique parent emails and IDs from active students
      const activeParentEmails = new Set();
      const activeParentIds = new Set();
      if (activeStudentsData) {
        activeStudentsData.forEach(student => {
          if (student.parent_email) {
            activeParentEmails.add(student.parent_email.toLowerCase().trim());
          }
          if (student.parent_id) {
            activeParentIds.add(student.parent_id);
          }
        });
      }
      
      // Query parents table - only get parents that have active students
      let query = supabase
        .from('parents')
        .select('id, name, email, phone, relationship, address, can_pickup, is_emergency_contact, pays_fees, is_primary')
        .eq('school_id', schoolData.id);
      
      // Filter to only parents with active students
      if (activeParentIds.size > 0) {
        query = query.in('id', Array.from(activeParentIds));
      } else if (activeParentEmails.size > 0) {
        // Fallback: filter by email if parent_id not available
        query = query.in('email', Array.from(activeParentEmails));
      } else {
        // No active students, so no active parents
        setExistingParents([]);
        setLoadingParents(false);
        return;
      }

      if (searchTerm.trim()) {
        query = query.or(`name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching parents:', error);
        // Fallback to students table if parents table doesn't exist yet
        // Only get parents from active students
        let fallbackQuery = supabase
          .from('students')
          .select('parent_name, parent_email, parent_phone, parent_relationship, parent_address, parent_can_pickup, parent_is_emergency_contact, parent_pays_fees, parent_is_primary')
          .eq('school_id', schoolData.id)
          .eq('is_active', true) // Only get parents from active students
          .not('parent_name', 'is', null)
          .not('parent_email', 'is', null);

        if (searchTerm.trim()) {
          fallbackQuery = fallbackQuery.or(`parent_name.ilike.%${searchTerm}%,parent_email.ilike.%${searchTerm}%`);
        }

        const { data: fallbackData, error: fallbackError } = await fallbackQuery;

        if (fallbackError) {
          console.error('Error fetching parents from fallback:', fallbackError);
          setExistingParents([]);
          return;
        }

        if (fallbackData) {
          const uniqueParents = fallbackData.reduce((acc, student) => {
            const existingParent = acc.find(p => p.email === student.parent_email);
            if (!existingParent) {
              acc.push({
                id: student.parent_email,
                name: student.parent_name,
                email: student.parent_email,
                phone: student.parent_phone,
                relationship: student.parent_relationship,
                address: student.parent_address,
                can_pickup: student.parent_can_pickup,
                is_emergency_contact: student.parent_is_emergency_contact,
                pays_fees: student.parent_pays_fees,
                is_primary: student.parent_is_primary
              });
            }
            return acc;
          }, []);
          setExistingParents(uniqueParents);
        } else {
          setExistingParents([]);
        }
        return;
      }

      if (data) {
        setExistingParents(data.map(p => ({
          id: p.id,
          name: p.name,
          email: p.email,
          phone: p.phone,
          relationship: p.relationship,
          address: p.address,
          can_pickup: p.can_pickup,
          is_emergency_contact: p.is_emergency_contact,
          pays_fees: p.pays_fees,
          is_primary: p.is_primary
        })));
      } else {
        setExistingParents([]);
      }
    } catch (err) {
      console.error('Error:', err);
      setExistingParents([]);
    } finally {
      setLoadingParents(false);
    }
  };

  const fetchSiblings = async (parentEmail) => {
    if (!schoolData?.id || !parentEmail) {
      setSiblings([]);
      return;
    }
    
    try {
      setLoadingSiblings(true);
      console.log('Fetching siblings for parent email:', parentEmail, 'in school:', schoolData.id);
      
      const { data, error } = await supabase
        .from('students')
        .select(`
          id,
          first_name,
          middle_name,
          last_name,
          email,
          class_id,
          section_id,
          date_of_birth,
          gender,
          is_active,
          classes(class_name)
        `)
        .eq('school_id', schoolData.id)
        .eq('parent_email', parentEmail.trim())
        .eq('is_active', true)
        .order('first_name');
      
      if (error) {
        console.error('Error fetching siblings:', error);
        setSiblings([]);
      } else {
        console.log('Found siblings:', data);
        setSiblings(data || []);
      }
    } catch (err) {
      console.error('Error:', err);
      setSiblings([]);
    } finally {
      setLoadingSiblings(false);
    }
  };

  const fetchTeachersForClassSection = async (classId, sectionId) => {
    if (!schoolData?.id || !classId || !sectionId) {
      setTeachers([]);
      return;
    }
    
    try {
      setLoadingTeachers(true);
      console.log('Fetching teachers for class:', classId, 'section:', sectionId);
      
      // Fetch all teachers for this school
      const { data: allTeachers, error } = await supabase
        .from('teachers')
        .select(`
          id,
          first_name,
          last_name,
          email,
          phone,
          qualification,
          subjects,
          class_ids,
          class_sections,
          is_active
        `)
        .eq('school_id', schoolData.id)
        .eq('is_active', true);
      
      if (error) {
        console.error('Error fetching teachers:', error);
        setTeachers([]);
        return;
      }
      
      // Filter teachers who are assigned to this class and section
      const matchingTeachers = (allTeachers || []).filter(teacher => {
        // Check if teacher has this class in their class_ids array
        const hasClass = teacher.class_ids && Array.isArray(teacher.class_ids) && teacher.class_ids.includes(parseInt(classId));
        
        if (!hasClass) return false;
        
        // Check if teacher has this section for this class in class_sections
        if (teacher.class_sections && typeof teacher.class_sections === 'object') {
          const classSections = teacher.class_sections[classId.toString()];
          if (Array.isArray(classSections)) {
            return classSections.includes(sectionId);
          }
        }
        
        return false;
      });
      
      console.log('Found teachers:', matchingTeachers);
      setTeachers(matchingTeachers);
    } catch (err) {
      console.error('Error:', err);
      setTeachers([]);
    } finally {
      setLoadingTeachers(false);
    }
  };

  const fetchClassFees = async (classId) => {
    if (!schoolData?.id || !classId) {
      setClassFees(null);
      setAdditionalFees([]);
      return;
    }
    
    try {
      setLoadingFees(true);
      console.log('Fetching fees for class:', classId);
      
      // Fetch main class fees
      const { data: feesData, error: feesError } = await supabase
        .from('class_fees')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('class_id', classId)
        .eq('is_active', true)
        .order('fee_type');
      
      if (feesError) {
        console.error('Error fetching class fees:', feesError);
        setClassFees(null);
      } else {
        console.log('Found class fees:', feesData);
        setClassFees(feesData && feesData.length > 0 ? feesData : null);
      }
      
      // Fetch additional fees
      const { data: additionalData, error: additionalError } = await supabase
        .from('additional_class_fees')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('class_id', classId)
        .eq('is_active', true);
      
      if (additionalError) {
        console.error('Error fetching additional fees:', additionalError);
        setAdditionalFees([]);
      } else {
        console.log('Found additional fees:', additionalData);
        setAdditionalFees(additionalData || []);
      }
    } catch (err) {
      console.error('Error:', err);
      setClassFees(null);
      setAdditionalFees([]);
    } finally {
      setLoadingFees(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setFormErrors(prev => ({
          ...prev,
          profilePicture: 'Please select a valid image file'
        }));
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setFormErrors(prev => ({
          ...prev,
          profilePicture: 'File size must be less than 5MB'
        }));
        return;
      }
      
      setStudentForm(prev => ({
        ...prev,
        profilePicture: file
      }));
      
      // Clear error
      if (formErrors.profilePicture) {
        setFormErrors(prev => ({
          ...prev,
          profilePicture: ''
        }));
      }
    }
  };

  const handleEmergencyDocumentsChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 10MB for documents)
      if (file.size > 10 * 1024 * 1024) {
        setFormErrors(prev => ({
          ...prev,
          emergencyDocuments: 'File size must be less than 10MB'
        }));
        return;
      }
      
      setStudentForm(prev => ({
        ...prev,
        emergencyDocuments: file
      }));
      
      // Clear error
      if (formErrors.emergencyDocuments) {
        setFormErrors(prev => ({
          ...prev,
          emergencyDocuments: ''
        }));
      }
    }
  };

  const handleDocumentAdd = (documentType, file) => {
    if (!file || !documentType) return;
    
    // Validate file size (max 10MB per document)
    if (file.size > 10 * 1024 * 1024) {
      alert(`File size must be less than 10MB for ${documentType}`);
      return;
    }
    
    setStudentForm(prev => ({
      ...prev,
      documents: [...prev.documents, { type: documentType, file: file, url: null }]
    }));
  };

  const handleDocumentRemove = (index) => {
    setStudentForm(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const handleDocumentTypeChange = (index, newType) => {
    setStudentForm(prev => ({
      ...prev,
      documents: prev.documents.map((doc, i) => 
        i === index ? { ...doc, type: newType } : doc
      )
    }));
  };

  // Generate password function: word + 2 digits (e.g., "book34")
  const generatePassword = () => {
    const words = ['book', 'star', 'moon', 'tree', 'bird', 'fish', 'rock', 'wind', 'fire', 'wave', 'lake', 'hill', 'rose', 'leaf', 'snow', 'rain', 'sun', 'sky', 'sea', 'gem'];
    const randomWord = words[Math.floor(Math.random() * words.length)];
    const randomDigits = Math.floor(Math.random() * 100).toString().padStart(2, '0');
    return randomWord + randomDigits;
  };

  const handleParentDocumentAdd = (parentIndex, documentType, file) => {
    if (!file || !documentType) return;
    
    // Validate file size (max 10MB per document)
    if (file.size > 10 * 1024 * 1024) {
      alert(`File size must be less than 10MB for ${documentType}`);
      return;
    }
    
    setStudentForm(prev => {
      const newParents = [...prev.parents];
      const parent = newParents[parentIndex];
      
      // Remove existing document of same type if exists
      const existingDocs = (parent.documents || []).filter(doc => doc.type !== documentType);
      
      newParents[parentIndex] = {
        ...parent,
        documents: [...existingDocs, { type: documentType, file: file, url: null, fileName: file.name }]
      };
      
      return {
        ...prev,
        parents: newParents
      };
    });
  };

  const handleParentDocumentRemove = (parentIndex, documentType) => {
    setStudentForm(prev => {
      const newParents = [...prev.parents];
      const parent = newParents[parentIndex];
      
      newParents[parentIndex] = {
        ...parent,
        documents: (parent.documents || []).filter(doc => doc.type !== documentType)
      };
      
      return {
        ...prev,
        parents: newParents
      };
    });
  };

  const handleParentSelectionToggle = (parentIndex, useExisting) => {
    setStudentForm(prev => {
      const newParents = [...prev.parents];
      newParents[parentIndex] = {
        ...newParents[parentIndex],
        useExisting: useExisting,
        existingParentId: useExisting ? newParents[parentIndex].existingParentId : null
      };
      return {
        ...prev,
        parents: newParents
      };
    });
    
    if (useExisting) {
      fetchExistingParents('', parentIndex);
    } else {
      setParentSearchTerms(prev => {
        const newTerms = [...prev];
        newTerms[parentIndex] = '';
        return newTerms;
      });
      setExistingParents([]);
    }
  };

  const handleParentSearch = (parentIndex, searchTerm) => {
    setParentSearchTerms(prev => {
      const newTerms = [...prev];
      newTerms[parentIndex] = searchTerm;
      return newTerms;
    });
    fetchExistingParents(searchTerm, parentIndex);
  };

  const handleParentSelect = async (parentIndex, parent) => {
    // Helper function to extract country code from phone number
    const extractCountryCode = (phoneNumber) => {
      if (!phoneNumber) return '';
      const countryCodes = ['+971', '+966', '+974', '+965', '+973', '+968', '+880', '+977', '+358', '+234', '+254', '+212', '+967', '+964', '+963', '+962', '+961', '+98', '+972', '+92', '+91', '+90', '+86', '+84', '+82', '+81', '+66', '+65', '+64', '+63', '+62', '+61', '+60', '+57', '+55', '+54', '+52', '+49', '+47', '+46', '+45', '+44', '+43', '+41', '+39', '+34', '+33', '+32', '+31', '+27', '+20', '+7', '+1'];
      for (const code of countryCodes) {
        if (phoneNumber.startsWith(code)) {
          return code;
        }
      }
      return '';
    };

    // Helper function to remove country code from phone number
    const removeCountryCode = (phoneNumber) => {
      if (!phoneNumber) return '';
      const countryCodes = ['+971', '+966', '+974', '+965', '+973', '+968', '+880', '+977', '+358', '+234', '+254', '+212', '+967', '+964', '+963', '+962', '+961', '+98', '+972', '+92', '+91', '+90', '+86', '+84', '+82', '+81', '+66', '+65', '+64', '+63', '+62', '+61', '+60', '+57', '+55', '+54', '+52', '+49', '+47', '+46', '+45', '+44', '+43', '+41', '+39', '+34', '+33', '+32', '+31', '+27', '+20', '+7', '+1'];
      for (const code of countryCodes) {
        if (phoneNumber.startsWith(code)) {
          return phoneNumber.substring(code.length).trim();
        }
      }
      return phoneNumber;
    };

    setStudentForm(prev => {
      const newParents = [...prev.parents];
      newParents[parentIndex] = {
        ...newParents[parentIndex],
        existingParentId: parent.id,
        name: parent.name,
        email: parent.email,
        phone: parent.phone || '',
        relationship: parent.relationship || '',
        address: parent.address || '',
        canPickup: parent.can_pickup || false,
        isEmergencyContact: parent.is_emergency_contact || false,
        paysFees: parent.pays_fees || false,
        primaryContact: parent.is_primary ? 'primary' : 'secondary',
        password: '', // Don't auto-fill password for security
        confirmPassword: '',
        documents: [] // Parent documents will be loaded separately if needed
      };

      // Auto-populate emergency contact fields with parent information
      const updatedForm = {
        ...prev,
        parents: newParents
      };

      // Always populate emergency contact fields when selecting an existing parent
      // Use parent's name and phone for emergency contact
      updatedForm.emergencyContact = parent.name || '';
      
      // Populate emergency phone and extract country code
      if (parent.phone) {
        const phoneCountryCode = extractCountryCode(parent.phone);
        const phoneWithoutCode = removeCountryCode(parent.phone);
        updatedForm.emergencyPhone = phoneWithoutCode;
        setReceptionistEmergencyPhoneCountryCode(phoneCountryCode);
      } else {
        updatedForm.emergencyPhone = '';
      }
      
      // If using parent address, update student address with selected parent's address
      if (prev.useParentAddress && parent.address) {
        updatedForm.selectedParentAddressIndex = parentIndex.toString();
        updatedForm.address = parent.address;
      }

      return updatedForm;
    });
    
    setParentSearchTerms(prev => {
      const newTerms = [...prev];
      newTerms[parentIndex] = `${parent.name} (${parent.email})`;
      return newTerms;
    });
    
    setExistingParents([]);
    
    // Fetch siblings for the selected parent
    fetchSiblings(parent.email);
    
    // Note: Parent documents are now uploaded per parent, not fetched from previous students
    // fetchParentDocuments(parent.email);
  };
  
  // Function to fetch parent documents from previous students
  const fetchParentDocuments = async (parentEmail) => {
    if (!schoolData?.id || !parentEmail) return;
    
    try {
      // First, find students that have this parent email
      const { data: studentsWithParent, error: studentsError } = await supabase
        .from('students')
        .select('id')
        .eq('school_id', schoolData.id)
        .eq('parent_email', parentEmail)
        .limit(10); // Limit to avoid too many queries
      
      if (studentsError) {
        console.error('Error fetching students with parent:', studentsError);
        return;
      }
      
      if (!studentsWithParent || studentsWithParent.length === 0) {
        return; // No students found with this parent
      }
      
      const studentIds = studentsWithParent.map(s => s.id);
      
      // Fetch documents of type "Parent National ID" or "Parent Passport" for these students
      const { data: parentDocuments, error: docsError } = await supabase
        .from('student_documents')
        .select('*')
        .in('student_id', studentIds)
        .in('document_type', ['Parent National ID', 'Parent Passport'])
        .order('uploaded_at', { ascending: false })
        .limit(2); // Get at most one of each type
      
      if (docsError) {
        console.error('Error fetching parent documents:', docsError);
        return;
      }
      
      if (!parentDocuments || parentDocuments.length === 0) {
        return; // No documents found
      }
      
      // Helper function to get valid file URL
      const getValidFileUrl = async (url) => {
        if (!url) return null;
        try {
          // If it's already a full URL, return it
          if (url.startsWith('http://') || url.startsWith('https://')) {
            return url;
          }
          // Otherwise, try to get signed URL
          const { data, error } = await supabase.storage
            .from('student-images')
            .createSignedUrl(url, 3600);
          if (error) {
            console.error('Error creating signed URL:', error);
            return url; // Return original URL as fallback
          }
          return data?.signedUrl || url;
        } catch (err) {
          console.error('Error in getValidFileUrl:', err);
          return url; // Return original URL as fallback
        }
      };
      
      // Generate signed URLs for documents
      const documentsWithUrls = await Promise.all(
        parentDocuments.map(async (doc) => {
          let documentUrl = doc.document_url;
          if (doc.document_url) {
            try {
              documentUrl = await getValidFileUrl(doc.document_url) || doc.document_url;
            } catch (err) {
              console.error('Error generating document URL:', err);
            }
          }
          return {
            ...doc,
            document_url: documentUrl
          };
        })
      );
      
      // Group documents by type to get one of each
      const nationalIdDoc = documentsWithUrls.find(doc => doc.document_type === 'Parent National ID');
      const passportDoc = documentsWithUrls.find(doc => doc.document_type === 'Parent Passport');
      
      // Add parent documents to studentForm.documents if they don't already exist
      setStudentForm(prev => {
        const existingDocs = prev.documents || [];
        const existingTypes = existingDocs.map(doc => doc.type || doc.document_type);
        
        const newDocs = [...existingDocs];
        
        // Add Parent National ID if not already present
        if (nationalIdDoc && !existingTypes.includes('Parent National ID')) {
          newDocs.push({
            type: 'Parent National ID',
            file: null, // This is an existing document, not a new file
            url: nationalIdDoc.document_url,
            existingDocument: true, // Flag to indicate this is from a previous student
            documentId: nationalIdDoc.id,
            fileName: nationalIdDoc.file_name || 'Parent National ID',
            fileSize: nationalIdDoc.file_size
          });
        }
        
        // Add Parent Passport if not already present
        if (passportDoc && !existingTypes.includes('Parent Passport')) {
          newDocs.push({
            type: 'Parent Passport',
            file: null, // This is an existing document, not a new file
            url: passportDoc.document_url,
            existingDocument: true, // Flag to indicate this is from a previous student
            documentId: passportDoc.id,
            fileName: passportDoc.file_name || 'Parent Passport',
            fileSize: passportDoc.file_size
          });
        }
        
        return {
          ...prev,
          documents: newDocs
        };
      });
    } catch (err) {
      console.error('Error fetching parent documents:', err);
    }
  };

  const handleClearParentSelection = (parentIndex) => {
    setStudentForm(prev => {
      const newParents = [...prev.parents];
      newParents[parentIndex] = {
        ...newParents[parentIndex],
        existingParentId: null,
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: ''
      };
      return {
        ...prev,
        parents: newParents
      };
    });
    
    setParentSearchTerms(prev => {
      const newTerms = [...prev];
      newTerms[parentIndex] = '';
      return newTerms;
    });
    setExistingParents([]);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!studentForm.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!studentForm.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!studentForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(studentForm.email)) {
      errors.email = 'Email is invalid';
    }
    
    // No password length or character restrictions - any password is acceptable
    if (!studentForm.password.trim()) {
      errors.password = 'Password is required';
    }
    
    if (!studentForm.confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (studentForm.password !== studentForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    if (!studentForm.dateOfBirth.trim()) {
      errors.dateOfBirth = 'Date of birth is required';
    }
    // Enforce minimum age of 2 years and maximum age of 100 years
    if (studentForm.dateOfBirth) {
      const dob = new Date(studentForm.dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
        age--;
      }
      if (age < 2) {
        errors.dateOfBirth = 'Student must be at least 2 years old';
      }
      if (age > 100) {
        errors.dateOfBirth = 'Student age cannot exceed 100 years';
      }
    }
    
    if (!studentForm.gender.trim()) {
      errors.gender = 'Gender is required';
    }
    
    if (!studentForm.nationality) {
      errors.nationality = 'Nationality is required';
    }
    
    // Validate branch selection when branches exist
    if (branches.length > 0 && !formBranchId) {
      errors.branchId = 'Branch selection is required';
    }
    
    // Class and Section are not required - they will be assigned during approval
    // Removed validation for classId and sectionId
    
    if (!studentForm.admissionDate.trim()) {
      errors.admissionDate = 'Admission date is required';
    } else if (studentForm.dateOfBirth) {
      // Ensure admission date is at least 2 years after date of birth
      try {
        const dob = new Date(studentForm.dateOfBirth);
        const admission = new Date(studentForm.admissionDate);

        if (!isNaN(dob.getTime()) && !isNaN(admission.getTime())) {
          const diffMs = admission.getTime() - dob.getTime();
          const diffYears = diffMs / (1000 * 60 * 60 * 24 * 365.25);

          if (diffYears < 2) {
            errors.admissionDate = 'Admission date must be at least 2 years after date of birth';
          }
        }
      } catch (e) {
        console.warn('Could not validate admission date vs DOB:', e);
      }
    }
    
    if (!studentForm.phone.trim()) {
      errors.phone = 'Student phone is required';
    }
    
    // Validate multiple parents
    if (!studentForm.parents || studentForm.parents.length === 0) {
      errors.parents = 'At least one parent is required';
    } else {
      studentForm.parents.forEach((parent, index) => {
        if (!parent.name.trim()) {
          errors[`parents.${index}.name`] = 'Parent name is required';
        }
        
        if (!parent.useExisting && !parent.relationship.trim()) {
          errors[`parents.${index}.relationship`] = 'Relationship is required';
        }
        
        if (!parent.email.trim()) {
          errors[`parents.${index}.email`] = 'Parent email is required';
        } else if (!/\S+@\S+\.\S+/.test(parent.email)) {
          errors[`parents.${index}.email`] = 'Parent email is invalid';
        }
        
        // Only validate passwords if not using existing parent
        // No password length or character restrictions - any password is acceptable
        if (!parent.useExisting) {
          if (!parent.password.trim()) {
            errors[`parents.${index}.password`] = 'Parent password is required';
          }
          
          if (!parent.confirmPassword.trim()) {
            errors[`parents.${index}.confirmPassword`] = 'Please confirm parent password';
          } else if (parent.password !== parent.confirmPassword) {
            errors[`parents.${index}.confirmPassword`] = 'Parent passwords do not match';
          }
        }
        
        // Validate existing parent selection
        if (parent.useExisting && !parent.existingParentId) {
          errors[`parents.${index}.selection`] = 'Please select an existing parent';
        }
        
        if (!parent.phone.trim()) {
          errors[`parents.${index}.phone`] = 'Parent phone is required';
        }
        if (!parent.address.trim()) {
          errors[`parents.${index}.address`] = 'Parent address is required';
        }
      });
    }
    
    if (!studentForm.emergencyContact.trim()) {
      errors.emergencyContact = 'Emergency contact is required';
    }
    
    if (!studentForm.emergencyPhone.trim()) {
      errors.emergencyPhone = 'Emergency phone is required';
    }
    
    // Validate pickup location - either select from list OR use student address
    if (studentForm.transportServiceEnabled) {
      if (!studentForm.pickupLocationId && !studentForm.useStudentAddressAsPickup) {
        errors.pickupLocationId = 'Please select a pickup location or use student address as pickup location when transport service is enabled';
      }
      
      // If using parent address, validate that parent address is provided
      if (studentForm.useStudentAddressAsPickup && studentForm.parents && studentForm.parents.length > 0 && !studentForm.parents[0].address.trim()) {
        errors[`parents.0.address`] = 'Please enter parent address to use as pickup location';
      }
    }
    
    // Route type and route selection are optional - no validation required
    
    setFormErrors(errors);
    
    // If there are errors, create a summary message
    if (Object.keys(errors).length > 0) {
      const errorFields = Object.keys(errors);
      const errorMessages = errorFields.map(field => {
        // Format field names for display
        const fieldName = field
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
          .replace(/parents\.\d+\./g, 'Parent ')
          .trim();
        return `${fieldName}: ${errors[field]}`;
      });
      
      const errorSummary = `Please fix the following errors:\n\n${errorMessages.join('\n')}`;
      setSubmissionError(errorSummary);
      
      // Scroll to the first error field
      setTimeout(() => {
        const firstErrorField = document.querySelector('.error');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstErrorField.focus();
        }
      }, 100);
      
      return false;
    }
    
    // Clear any previous submission errors
    setSubmissionError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous submission errors
    setSubmissionError('');
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Try to get user, but don't block uploads if auth check fails
      // Supabase storage RLS policies will handle permissions
      const { data: { user }, error: authCheckError } = await supabase.auth.getUser();
      if (authCheckError) {
        console.warn('Auth check warning (continuing anyway):', authCheckError);
      }
      
      let profilePictureUrl = null;
      let emergencyDocumentsUrl = null;
      
      // Upload profile picture if provided
      if (studentForm.profilePicture) {
        try {
        const fileExt = studentForm.profilePicture.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `student-profiles/${schoolData.id}/${fileName}`;
        
          console.log('Uploading profile picture:', {
            fileName,
            filePath,
            fileSize: studentForm.profilePicture.size,
            fileType: studentForm.profilePicture.type,
            userId: user?.id || 'anonymous'
          });
          
          const { data: uploadData, error: uploadError } = await supabase.storage
          .from('student-images')
            .upload(filePath, studentForm.profilePicture, {
              cacheControl: '3600',
              upsert: false
            });
        
        if (uploadError) {
            console.error('Upload error details:', uploadError);
            console.error('Upload error message:', uploadError.message);
            console.error('Upload error status:', uploadError.statusCode);
            
            // Check for specific error types
            if (uploadError.message.includes('row-level security') || 
                uploadError.message.includes('RLS') ||
                uploadError.message.includes('policy')) {
              alert('Storage permission error: Please contact administrator to set up storage policies. Error: ' + uploadError.message);
            } else if (uploadError.message.includes('bucket') || uploadError.message.includes('not found')) {
              alert('Storage bucket not found. Please contact administrator to set up the student-images bucket.');
            } else if (uploadError.message.includes('permission') || uploadError.message.includes('unauthorized')) {
              alert('Permission denied. Please contact administrator to check storage permissions.');
            } else {
              alert(`Failed to upload profile picture: ${uploadError.message}`);
            }
            setIsSubmitting(false);
          return;
        }
          
          console.log('Upload successful:', uploadData);
        
        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('student-images')
          .getPublicUrl(filePath);
        
        profilePictureUrl = publicUrl;
          console.log('Public URL generated:', publicUrl);
        } catch (uploadErr) {
          console.error('Exception during profile picture upload:', uploadErr);
          alert('An error occurred while uploading the profile picture. Please try again.');
          setIsSubmitting(false);
          return;
        }
      }
      
      // Upload emergency documents if provided
      if (studentForm.emergencyDocuments) {
        try {
        const fileExt = studentForm.emergencyDocuments.name.split('.').pop();
        const fileName = `emergency_docs_${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `emergency-documents/${schoolData.id}/${fileName}`;
        
          console.log('Uploading emergency documents:', {
            fileName,
            filePath,
            fileSize: studentForm.emergencyDocuments.size,
            fileType: studentForm.emergencyDocuments.type
          });
          
          const { data: uploadData, error: uploadError } = await supabase.storage
          .from('student-images')
            .upload(filePath, studentForm.emergencyDocuments, {
              cacheControl: '3600',
              upsert: false
            });
        
        if (uploadError) {
            console.error('Emergency documents upload error details:', uploadError);
            console.error('Emergency documents upload error message:', uploadError.message);
            
            // Check for specific error types
            if (uploadError.message.includes('row-level security') || 
                uploadError.message.includes('RLS') ||
                uploadError.message.includes('policy')) {
              alert('Storage permission error: Please contact administrator to set up storage policies. Error: ' + uploadError.message);
            } else if (uploadError.message.includes('bucket') || uploadError.message.includes('not found')) {
              alert('Storage bucket not found. Please contact administrator to set up the student-images bucket.');
            } else if (uploadError.message.includes('permission') || uploadError.message.includes('unauthorized')) {
              alert('Permission denied. Please contact administrator to check storage permissions.');
            } else {
              alert(`Failed to upload emergency documents: ${uploadError.message}`);
            }
            setIsSubmitting(false);
          return;
        }
          
          console.log('Emergency documents upload successful:', uploadData);
        
        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('student-images')
          .getPublicUrl(filePath);
        
        emergencyDocumentsUrl = publicUrl;
          console.log('Emergency documents public URL generated:', publicUrl);
        } catch (uploadErr) {
          console.error('Exception during emergency documents upload:', uploadErr);
          alert('An error occurred while uploading the emergency documents. Please try again.');
          setIsSubmitting(false);
          return;
        }
      }
      
      let authUserId = null;
      let studentAuthError = null;
      
      // Try to create Supabase Auth user for student
      try {
        console.log('Attempting to create auth user for student:', studentForm.email);
        
        // Try to create new student auth user
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: studentForm.email,
          password: studentForm.password,
          options: {
            data: {
              first_name: studentForm.firstName,
              last_name: studentForm.lastName,
              user_type: 'student'
            },
            emailRedirectTo: undefined // Disable email confirmation redirect
          }
        });
          
          if (authError) {
            console.error('Student auth user creation error:', authError);
            studentAuthError = authError;
            
            // Email strictness removed - allow same email for different user types
            // Check for specific error types
            if (authError.message.includes('already registered') || 
                authError.message.includes('already exists') ||
                authError.message.includes('User already registered')) {
              // Try to get the existing user's ID from users table
              const { data: existingUserRecord } = await supabase
                .from('users')
                .select('id')
                .eq('email', studentForm.email)
                .eq('user_type', 'student')
                .maybeSingle();
              
              if (existingUserRecord?.id) {
                authUserId = existingUserRecord.id;
                console.log('Found existing student user, using ID:', authUserId);
                studentAuthError = null; // Clear error since we found the user
              } else {
                // Email may be used for another user type (teacher, parent, etc.) - that's okay
                console.log('Email already registered in auth.users (may be used for another user type), continuing without auth user');
                studentAuthError = null; // Clear error and continue
              }
            } else {
              // For other errors, show warning but continue
              console.warn('Student auth creation failed, but continuing:', authError.message);
            }
          } else if (authData?.user?.id) {
            authUserId = authData.user.id;
            console.log('Student auth user created successfully:', authUserId);
          } else if (authData?.user === null && !authError) {
            // This might happen if email confirmation is required
            // Check if user was created but needs confirmation
            console.warn('Auth user creation returned null user - email confirmation may be required');
            // Try to find the user in auth.users via admin API or continue without auth
          }
          
          // Ensure student user record exists in users table
          if (authUserId && !studentAuthError) {
          try {
            // Use upsert to ensure the record exists (creates if missing, updates if exists)
            const { data: userRecord, error: userError } = await supabase
              .from('users')
              .upsert({
                id: authUserId,
                email: studentForm.email,
                first_name: studentForm.firstName,
                last_name: studentForm.lastName,
                user_type: 'student'
              }, {
                onConflict: 'id'
              })
              .select()
              .single();
            
            if (userError) {
              console.error('Failed to create/update student user record:', userError);
              // Try one more time with insert
              try {
                const { data: retryRecord, error: retryError } = await supabase
                  .from('users')
                  .insert({
                    id: authUserId,
                    email: studentForm.email,
                    first_name: studentForm.firstName,
                    last_name: studentForm.lastName,
                    user_type: 'student'
                  })
                  .select()
                  .single();
                
                if (retryError) {
                  console.error('Retry also failed:', retryError);
                } else {
                  console.log('Student user record created on retry:', retryRecord);
                }
              } catch (retryErr) {
                console.error('Retry exception:', retryErr);
              }
            } else {
              console.log('Student user record created/updated successfully:', userRecord);
            }
          } catch (userErr) {
            console.error('Student user record operation failed:', userErr);
            // Continue anyway - auth user exists and can login
          }
        }
      } catch (authErr) {
        console.error('Student auth user creation exception:', authErr);
        studentAuthError = authErr;
        // Continue without auth user - student can still be added
      }

      // Note: Parent auth creation is now handled in the multiple parents saving section below
      // This old single-parent code is kept for reference but is no longer used
      // Parent authentication is now handled when saving each parent in the parents array
      
      // Skip old single parent auth creation - now handled in multiple parents section
      /*
      let parentAuthUserId = null;
      let parentAuthError = null;
      
      if (false) { // Disabled - using new multiple parents system
        try {
          console.log('Creating Supabase auth user for parent:', studentForm.parentEmail);
          
          // Try to create new parent user directly (don't check login first as it might interfere)
          const { data: parentAuthData, error: parentSignUpError } = await supabase.auth.signUp({
            email: studentForm.parentEmail,
            password: studentForm.parentPassword,
            options: {
              data: {
                first_name: studentForm.parentName.split(' ')[0] || studentForm.parentName,
                last_name: studentForm.parentName.split(' ').slice(1).join(' ') || '',
                user_type: 'parent',
                child_email: studentForm.email,
                school_id: schoolData.id
              },
              emailRedirectTo: undefined // Disable email confirmation redirect
            }
          });
          
          if (parentSignUpError) {
            console.error('Parent auth user creation error:', parentSignUpError);
            
            // Check for specific error types
            if (parentSignUpError.message.includes('already registered') || 
                parentSignUpError.message.includes('already exists') ||
                parentSignUpError.message.includes('User already registered')) {
              // User already exists, try to get their ID
              try {
                // Try to sign in to get the user ID
                const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
                  email: studentForm.parentEmail,
                  password: studentForm.parentPassword
                });
                
                if (signInData?.user && !signInError) {
                  parentAuthUserId = signInData.user.id;
                  console.log('Parent user already exists and can login, using ID:', parentAuthUserId);
                  parentAuthError = null;
                  // Sign out after getting the ID
                  await supabase.auth.signOut();
                } else {
                  // Can't login with provided password - might be wrong password
                  alert(`Parent email ${studentForm.parentEmail} is already registered but password doesn't match. Please use a different email or select existing parent.`);
                  setIsSubmitting(false);
                  return;
                }
              } catch (signInErr) {
                console.error('Error signing in existing parent:', signInErr);
                alert(`Parent email ${studentForm.parentEmail} is already registered. Please use a different email or select existing parent.`);
                setIsSubmitting(false);
                return;
              }
            } else {
              // For other errors, show warning but continue
              console.warn('Parent auth creation failed:', parentSignUpError.message);
              parentAuthError = parentSignUpError;
            }
          } else if (parentAuthData?.user?.id) {
            parentAuthUserId = parentAuthData.user.id;
            console.log('Parent auth user created successfully:', parentAuthUserId);
            // Sign out after creation (we'll sign them in when they login)
            await supabase.auth.signOut();
          } else if (parentAuthData?.user === null && !parentSignUpError) {
            // This might happen if email confirmation is required
            // But the user might still be created - check auth.users table
            console.warn('Parent auth user creation returned null user - checking if user was created');
            // Try to sign in to verify account was created
            try {
              const { data: verifyData, error: verifyError } = await supabase.auth.signInWithPassword({
                email: studentForm.parentEmail,
                password: studentForm.parentPassword
              });
              
              if (verifyData?.user && !verifyError) {
                parentAuthUserId = verifyData.user.id;
                console.log('Parent account was created and can login:', parentAuthUserId);
                await supabase.auth.signOut();
              } else {
                console.warn('Parent account may need email confirmation:', verifyError?.message);
                parentAuthError = new Error('Parent account created but may need email confirmation');
              }
            } catch (verifyErr) {
              console.warn('Could not verify parent account:', verifyErr);
              parentAuthError = new Error('Parent account creation status unknown');
            }
          }
          
          // Ensure parent user record exists in users table
          if (parentAuthUserId && !parentAuthError) {
            try {
              // Use upsert to ensure the record exists (creates if missing, updates if exists)
              const { data: parentUserRecord, error: parentUserError } = await supabase
                .from('users')
                .upsert({
                  id: parentAuthUserId,
                  email: studentForm.parentEmail,
                  first_name: studentForm.parentName.split(' ')[0] || studentForm.parentName,
                  last_name: studentForm.parentName.split(' ').slice(1).join(' ') || '',
                  user_type: 'parent'
                }, {
                  onConflict: 'id'
                })
                .select()
                .single();
              
              if (parentUserError) {
                console.error('Failed to create/update parent user record:', parentUserError);
                // Try one more time with insert
                try {
                  const { data: retryRecord, error: retryError } = await supabase
                    .from('users')
                    .insert({
                      id: parentAuthUserId,
                      email: studentForm.parentEmail,
                      first_name: studentForm.parentName.split(' ')[0] || studentForm.parentName,
                      last_name: studentForm.parentName.split(' ').slice(1).join(' ') || '',
                      user_type: 'parent'
                    })
                    .select()
                    .single();
                  
                  if (retryError) {
                    console.error('Retry also failed:', retryError);
                  } else {
                    console.log('Parent user record created on retry:', retryRecord);
                  }
                } catch (retryErr) {
                  console.error('Retry exception:', retryErr);
                }
              } else {
                console.log('Parent user record created/updated successfully:', parentUserRecord);
              }
            } catch (parentUserErr) {
              console.error('Parent user record operation failed:', parentUserErr);
              // Continue anyway - auth user exists and can login
            }
          }
        } catch (parentAuthErr) {
          console.error('Parent auth user creation exception:', parentAuthErr);
          parentAuthError = parentAuthErr;
          // Continue without parent auth user - student can still be added
        }
      } else {
        console.log('Using existing parent - skipping parent auth user creation');
        console.log('New student will be a sibling of existing students with parent email:', studentForm.parents?.[0]?.email);
      }
      */
      
      // Calculate fee information
      let monthlyTuitionFee = 0;
      let transportFee = 0;
      let monthlyAdditionalFees = 0;
      let oneTimeFees = 0;
      let totalMonthlyFee = 0;
      let feeCurrency = 'PKR';
      let baseFeeType = null;
      let baseFeeAmount = null;

      // Calculate fees if class is selected and fees are loaded
      if (studentForm.classId && classFees && Array.isArray(classFees) && classFees.length > 0) {
        const fee = classFees[0]; // Get the first fee record for the class
        feeCurrency = fee.currency || 'PKR';
        baseFeeType = fee.fee_type || null;
        baseFeeAmount = parseFloat(fee.amount) || 0;

        // Calculate monthly tuition based on fee type
        switch (fee.fee_type) {
          case 'monthly':
            monthlyTuitionFee = baseFeeAmount;
            break;
          case 'quarterly':
            monthlyTuitionFee = baseFeeAmount / 3;
            break;
          case 'semester':
            monthlyTuitionFee = baseFeeAmount / 6;
            break;
          case 'annual':
            monthlyTuitionFee = baseFeeAmount / 12;
            break;
          default:
            monthlyTuitionFee = baseFeeAmount;
        }

        // Calculate transport fee
        if (studentForm.transportServiceEnabled && studentForm.pickupLocationId && studentForm.transportRouteType) {
          const selectedLocation = pickupLocations.find(loc => loc.id.toString() === studentForm.pickupLocationId);
          if (selectedLocation) {
            if (studentForm.transportRouteType === '1 Way' && selectedLocation.price_one_way) {
              transportFee = parseFloat(selectedLocation.price_one_way);
            } else if (studentForm.transportRouteType === '2 Way' && selectedLocation.price_two_way) {
              transportFee = parseFloat(selectedLocation.price_two_way);
            }
          }
        }

        // Calculate additional fees
        if (additionalFees && Array.isArray(additionalFees) && additionalFees.length > 0) {
          monthlyAdditionalFees = additionalFees
            .filter(af => af.period === 'monthly')
            .reduce((sum, af) => sum + (parseFloat(af.amount) || 0), 0);
          
          oneTimeFees = additionalFees
            .filter(af => af.period === 'one_time')
            .reduce((sum, af) => sum + (parseFloat(af.amount) || 0), 0);
        }

        // Calculate total monthly fee
        totalMonthlyFee = monthlyTuitionFee + transportFee + monthlyAdditionalFees;
      }

      console.log('Calculated fee information:', {
        monthlyTuitionFee,
        transportFee,
        monthlyAdditionalFees,
        oneTimeFees,
        totalMonthlyFee,
        feeCurrency,
        baseFeeType,
        baseFeeAmount
      });
      
      // Get receptionist data - this is always a receptionist adding the student
      const receptionistData = localStorage.getItem('receptionistData');
      let createdByReceptionistId = null;
      if (receptionistData) {
        try {
          const receptionist = JSON.parse(receptionistData);
          createdByReceptionistId = receptionist.id;
          console.log('Student being added by receptionist:', receptionist.id);
        } catch (err) {
          console.error('Error parsing receptionist data:', err);
        }
      }

      // Always require approval for students added by receptionists
      const isApproved = false;

      // Check for duplicate student before inserting
      const checkDuplicateStudent = async () => {
        const duplicateChecks = [];
        
        // Check 1: Email match
        if (studentForm.email.trim()) {
          const { data: emailMatch } = await supabase
            .from('students')
            .select('id, first_name, last_name, email, phone')
            .eq('school_id', schoolData.id)
            .eq('email', studentForm.email.trim().toLowerCase())
            .maybeSingle();
          
          if (emailMatch) {
            duplicateChecks.push(`A student with email "${studentForm.email}" already exists (${emailMatch.first_name} ${emailMatch.last_name})`);
          }
        }
        
        // Check 2: Phone match
        if (studentForm.phone.trim()) {
          const phoneWithCode = receptionistStudentPhoneCountryCode 
            ? `${receptionistStudentPhoneCountryCode} ${studentForm.phone.trim()}`
            : studentForm.phone.trim();
          const { data: phoneMatch } = await supabase
            .from('students')
            .select('id, first_name, last_name, email, phone')
            .eq('school_id', schoolData.id)
            .eq('phone', phoneWithCode)
            .maybeSingle();
          
          if (phoneMatch) {
            duplicateChecks.push(`A student with phone "${phoneWithCode}" already exists (${phoneMatch.first_name} ${phoneMatch.last_name})`);
          }
        }
        
        // Check 3: First Name + Middle Name + Last Name match (exact duplicate)
        if (studentForm.firstName.trim() && studentForm.lastName.trim()) {
          // Build query for matching first and last name
          let nameQuery = supabase
            .from('students')
            .select('id, first_name, middle_name, last_name, email')
            .eq('school_id', schoolData.id)
            .eq('first_name', studentForm.firstName.trim())
            .eq('last_name', studentForm.lastName.trim());
          
          const { data: nameMatches } = await nameQuery;
          
          if (nameMatches && nameMatches.length > 0) {
            // Filter by middle name match
            const trimmedMiddleName = studentForm.middleName.trim();
            const exactMatches = nameMatches.filter(student => {
              const studentMiddleName = student.middle_name ? student.middle_name.trim() : '';
              // Both have middle name and they match, or both are empty/null
              if (trimmedMiddleName && studentMiddleName) {
                return trimmedMiddleName.toLowerCase() === studentMiddleName.toLowerCase();
              } else if (!trimmedMiddleName && !studentMiddleName) {
                return true;
              }
              return false;
            });
            
            if (exactMatches.length > 0) {
              const fullName = `${studentForm.firstName.trim()}${trimmedMiddleName ? ' ' + trimmedMiddleName : ''} ${studentForm.lastName.trim()}`.trim();
              duplicateChecks.push(`A duplicate student already exists with the same name: "${fullName}". Please use a different name or check existing student records.`);
            }
          }
        }
        
        // Check 4: Name + Date of Birth + Parent Email/Phone match
        if (studentForm.firstName.trim() && studentForm.lastName.trim() && studentForm.dateOfBirth) {
          const { data: nameDobMatches } = await supabase
            .from('students')
            .select('id, first_name, last_name, email, date_of_birth')
            .eq('school_id', schoolData.id)
            .eq('first_name', studentForm.firstName.trim())
            .eq('last_name', studentForm.lastName.trim())
            .eq('date_of_birth', studentForm.dateOfBirth);
          
          if (nameDobMatches && nameDobMatches.length > 0) {
            // Check if any of these students have matching parent email or phone
            const parentEmails = studentForm.parents
              .filter(p => !p.useExisting && p.email && p.email.trim())
              .map(p => p.email.trim().toLowerCase());
            
            const parentPhones = studentForm.parents
              .filter(p => !p.useExisting && p.phone && p.phone.trim())
              .map((p, index) => {
                const countryCode = receptionistParentPhoneCountryCodes[index] || '';
                return `${countryCode} ${p.phone.trim()}`;
              });
            
            if (parentEmails.length > 0 || parentPhones.length > 0) {
              // Get parent relationships for these students
              const studentIds = nameDobMatches.map(s => s.id);
              const { data: parentRelations } = await supabase
                .from('student_parents')
                .select('student_id, parent_id, parents!inner(email, phone)')
                .in('student_id', studentIds);
              
              if (parentRelations && parentRelations.length > 0) {
                const matchingParents = parentRelations.filter(rel => {
                  const parent = rel.parents;
                  return (
                    (parentEmails.length > 0 && parent.email && parentEmails.includes(parent.email.toLowerCase())) ||
                    (parentPhones.length > 0 && parent.phone && parentPhones.some(pp => parent.phone.includes(pp.split(' ')[1])))
                  );
                });
                
                if (matchingParents.length > 0) {
                  const matchedStudent = nameDobMatches.find(s => 
                    matchingParents.some(mp => mp.student_id === s.id)
                  );
                  if (matchedStudent) {
                    duplicateChecks.push(`A student with the same name (${studentForm.firstName} ${studentForm.lastName}), date of birth (${studentForm.dateOfBirth}), and parent contact information already exists`);
                  }
                }
              }
            }
          }
        }
        
        return duplicateChecks;
      };
      
      // Check for duplicates
      const duplicateErrors = await checkDuplicateStudent();
      if (duplicateErrors.length > 0) {
        setIsSubmitting(false);
        const errorMessage = `Duplicate student detected:\n\n${duplicateErrors.join('\n\n')}\n\nPlease check the existing student records or use different information.`;
        setSubmissionError(errorMessage);
        setTimeout(() => {
          const errorElement = document.querySelector('.submission-error-summary');
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return;
      }

      // Prepare student data for database
      const studentData = {
        school_id: schoolData.id,
        branch_id: formBranchId || null, // Include branch_id if a branch is selected
        auth_user_id: authUserId, // This can now be null
        created_by_receptionist_id: createdByReceptionistId, // Track which receptionist added this student
        is_approved: isApproved, // Auto-approve if added by school, require approval if added by receptionist
        first_name: studentForm.firstName.trim(),
        middle_name: studentForm.middleName.trim() || null,
        last_name: studentForm.lastName.trim(),
        email: studentForm.email.trim(),
        date_of_birth: studentForm.dateOfBirth,
        nationality: studentForm.nationality.trim() || null,
        phone: studentForm.phone.trim() 
          ? (receptionistStudentPhoneCountryCode ? `${receptionistStudentPhoneCountryCode} ${studentForm.phone.trim()}` : studentForm.phone.trim())
          : '',
        gender: studentForm.gender.trim(),
        class_id: studentForm.classId || null, // Will be assigned during approval if not set
        section_id: studentForm.sectionId ? studentForm.sectionId.trim() : null, // Will be assigned during approval if not set
        admission_date: studentForm.admissionDate,
        previous_school: studentForm.previousSchool.trim() || null,
        previous_school_grade: studentForm.previousSchoolGrade.trim() || null,
        student_id_no: studentForm.studentIdNo.trim() || null,
        // Note: Parent data is now stored in separate parents table
        // Keeping these fields for backward compatibility but they may be null
        parent_name: studentForm.parents && studentForm.parents.length > 0 ? studentForm.parents[0].name.trim() : null,
        parent_email: studentForm.parents && studentForm.parents.length > 0 ? studentForm.parents[0].email.trim() : null,
        parent_phone: studentForm.parents && studentForm.parents.length > 0 && studentForm.parents[0].phone.trim() 
          ? `${receptionistParentPhoneCountryCodes[0] || ''} ${studentForm.parents[0].phone.trim()}`.trim() : null,
        parent_relationship: studentForm.parents && studentForm.parents.length > 0 ? studentForm.parents[0].relationship.trim() || null : null,
        parent_address: studentForm.parents && studentForm.parents.length > 0 ? studentForm.parents[0].address.trim() || null : null,
        parent_is_primary: studentForm.parents && studentForm.parents.length > 0 ? studentForm.parents[0].primaryContact === 'primary' : false,
        parent_can_pickup: studentForm.parents && studentForm.parents.length > 0 ? !!studentForm.parents[0].canPickup : false,
        parent_is_emergency_contact: studentForm.parents && studentForm.parents.length > 0 ? !!studentForm.parents[0].isEmergencyContact : false,
        parent_pays_fees: studentForm.parents && studentForm.parents.length > 0 ? !!studentForm.parents[0].paysFees : false,
        address: studentForm.address.trim() || '',
        emergency_contact: studentForm.emergencyContact.trim(),
        emergency_phone: studentForm.emergencyPhone.trim() ? `${receptionistEmergencyPhoneCountryCode} ${studentForm.emergencyPhone.trim()}` : '',
        profile_picture_url: profilePictureUrl,
        // Medical Details
        medical_details: studentForm.medicalDetails.trim() || null,
        emergency_documents_url: emergencyDocumentsUrl,
        // Transport Service
        transport_service_enabled: !!studentForm.transportServiceEnabled,
        pickup_location_id: studentForm.transportServiceEnabled && studentForm.pickupLocationId && !studentForm.useStudentAddressAsPickup
          ? parseInt(studentForm.pickupLocationId, 10) 
          : null, // Will be set after creating/finding pickup location if using student address
        transport_route_type: studentForm.transportServiceEnabled && studentForm.transportRouteType 
          ? studentForm.transportRouteType 
          : null,
        transport_time: studentForm.transportServiceEnabled && studentForm.transportRouteType === '1 Way' && studentForm.transportTime
          ? studentForm.transportTime 
          : null,
        transport_price: studentForm.transportServiceEnabled && studentForm.pickupLocationId && studentForm.transportRouteType
          ? (() => {
              const selectedLocation = pickupLocations.find(loc => loc.id.toString() === studentForm.pickupLocationId);
              if (selectedLocation) {
                if (studentForm.transportRouteType === '1 Way' && selectedLocation.price_one_way) {
                  return parseFloat(selectedLocation.price_one_way);
                } else if (studentForm.transportRouteType === '2 Way' && selectedLocation.price_two_way) {
                  return parseFloat(selectedLocation.price_two_way);
                }
              }
              return null;
            })()
          : null,
        route_id: studentForm.transportServiceEnabled && studentForm.routeId 
          ? parseInt(studentForm.routeId, 10) 
          : null,
        // Payment Method
        payment_method: studentForm.paymentMethod.trim() || null,
        // Fee Information
        monthly_tuition_fee: monthlyTuitionFee > 0 ? monthlyTuitionFee : null,
        transport_fee: transportFee > 0 ? transportFee : null,
        monthly_additional_fees: monthlyAdditionalFees > 0 ? monthlyAdditionalFees : null,
        one_time_fees: oneTimeFees > 0 ? oneTimeFees : null,
        total_monthly_fee: totalMonthlyFee > 0 ? totalMonthlyFee : null,
        fee_currency: feeCurrency,
        base_fee_type: baseFeeType,
        base_fee_amount: baseFeeAmount,
        is_active: true,
        created_at: new Date().toISOString()
      };
      
      console.log('Saving student data to Supabase:', studentData);
      console.log('Class ID being saved:', studentData.class_id);
      console.log('Class ID type:', typeof studentData.class_id);
      
      // Upload documents before saving student
      const uploadedDocuments = [];
      if (studentForm.documents && studentForm.documents.length > 0) {
        for (const doc of studentForm.documents) {
          if (doc.file) {
            try {
              const fileExt = doc.file.name.split('.').pop();
              const fileName = `${doc.type.replace(/\s+/g, '_')}_${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
              const filePath = `student-documents/${schoolData.id}/${fileName}`;
              
              const { error: uploadError } = await supabase.storage
                .from('student-images')
                .upload(filePath, doc.file);
              
              if (uploadError) {
                console.error('Document upload error:', uploadError);
                console.error('File path:', filePath);
                console.error('File name:', doc.file.name);
                console.error('File size:', doc.file.size);
                alert(`Failed to upload ${doc.type}. Error: ${uploadError.message || uploadError}. Please check Supabase Storage setup.`);
                setIsSubmitting(false);
                return;
              }
              
              // Get public URL
              const { data: { publicUrl } } = supabase.storage
                .from('student-images')
                .getPublicUrl(filePath);
              
              uploadedDocuments.push({
                type: doc.type,
                url: publicUrl,
                fileName: doc.file.name,
                fileSize: doc.file.size
              });
            } catch (uploadErr) {
              console.error('Document upload exception:', uploadErr);
              alert(`Failed to upload ${doc.type}. Error: ${uploadErr.message || uploadErr}. Please check Supabase Storage setup.`);
              setIsSubmitting(false);
              return;
            }
          }
        }
      }
      
      let data, error;
      
      if (isEditMode && studentToEdit?.id) {
        // Update existing student
        const updateData = { ...studentData };
        delete updateData.created_at; // Don't update created_at
        updateData.updated_at = new Date().toISOString();
        
        const result = await supabase
          .from('students')
          .update(updateData)
          .eq('id', studentToEdit.id)
          .select()
          .single();
        
        data = result.data;
        error = result.error;
        
        if (error) {
          console.error('Error updating student:', error);
          alert(`Failed to update student: ${error.message}`);
          setIsSubmitting(false);
          return;
        }
        
        console.log('Student updated successfully:', data);
        
        // Handle pickup location - create one if using parent address (for edit mode)
        if (studentForm.transportServiceEnabled && studentForm.useStudentAddressAsPickup && studentForm.parents && studentForm.parents.length > 0 && studentForm.parents[0].address.trim() && data?.id) {
          try {
            const parentAddress = studentForm.parents[0].address.trim();
            // Check if a pickup location with this address already exists for this school
            const { data: existingLocation, error: checkError } = await supabase
              .from('pickup_locations')
              .select('id')
              .eq('school_id', schoolData.id)
              .eq('address', parentAddress)
              .maybeSingle();
            
            if (checkError && checkError.code !== 'PGRST116') {
              console.error('Error checking existing pickup location:', checkError);
            }
            
            let finalPickupLocationId = null;
            
            if (existingLocation) {
              // Use existing pickup location
              finalPickupLocationId = existingLocation.id;
              console.log('Using existing pickup location with address:', parentAddress);
            } else {
              // Create new pickup location with parent address
              const locationName = `Parent Address - ${studentForm.firstName} ${studentForm.lastName}`;
              const { data: newLocation, error: createError } = await supabase
                .from('pickup_locations')
                .insert({
                  school_id: schoolData.id,
                  location_name: locationName,
                  address: parentAddress,
                  is_active: true
                })
                .select()
                .single();
              
              if (createError) {
                console.error('Error creating pickup location:', createError);
                alert(`Warning: Student was updated but failed to create pickup location: ${createError.message}`);
              } else {
                finalPickupLocationId = newLocation.id;
                console.log('Created new pickup location with parent address:', newLocation.id);
              }
            }
            
            // Update student with the pickup location ID
            if (finalPickupLocationId) {
              const updateResult = await supabase
                .from('students')
                .update({ pickup_location_id: finalPickupLocationId })
                .eq('id', data.id);
              
              if (updateResult.error) {
                console.error('Error updating student with pickup location:', updateResult.error);
              }
            }
          } catch (locationErr) {
            console.error('Exception handling pickup location:', locationErr);
            alert(`Warning: Student was updated but there was an error setting up pickup location: ${locationErr.message}`);
          }
        }
      } else {
        // Insert new student
        const result = await supabase
          .from('students')
          .insert(studentData)
          .select()
          .single();
        
        data = result.data;
        error = result.error;
        
        if (error) {
          console.error('Error saving student:', error);
          
          // If it's a foreign key constraint error, try to clean up the auth user
          if (error.code === '23503') {
            console.log('Attempting to clean up auth user due to foreign key constraint error');
            // Note: We can't delete auth users from the client side, but we can inform the user
            setSubmissionError('There was an issue linking the student account. The email account was created but student record failed. Please contact support or try with a different email.');
          } else {
            setSubmissionError(`Failed to add student: ${error.message}`);
          }
          setIsSubmitting(false);
          
          // Scroll to error message
          setTimeout(() => {
            const errorElement = document.querySelector('.submission-error-summary');
            if (errorElement) {
              errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          return;
        }
        
        console.log('Student added successfully:', data);
        
        // Handle pickup location - create one if using parent address
        let finalPickupLocationId = studentForm.pickupLocationId ? parseInt(studentForm.pickupLocationId, 10) : null;
        
        if (studentForm.transportServiceEnabled && studentForm.useStudentAddressAsPickup && studentForm.parents && studentForm.parents.length > 0 && studentForm.parents[0].address.trim() && data?.id) {
          try {
            const parentAddress = studentForm.parents[0].address.trim();
            // Check if a pickup location with this address already exists for this school
            const { data: existingLocation, error: checkError } = await supabase
              .from('pickup_locations')
              .select('id')
              .eq('school_id', schoolData.id)
              .eq('address', parentAddress)
              .maybeSingle();
            
            if (checkError && checkError.code !== 'PGRST116') {
              console.error('Error checking existing pickup location:', checkError);
            }
            
            if (existingLocation) {
              // Use existing pickup location
              finalPickupLocationId = existingLocation.id;
              console.log('Using existing pickup location with address:', parentAddress);
            } else {
              // Create new pickup location with parent address
              const locationName = `Parent Address - ${studentForm.firstName} ${studentForm.lastName}`;
              const { data: newLocation, error: createError } = await supabase
                .from('pickup_locations')
                .insert({
                  school_id: schoolData.id,
                  location_name: locationName,
                  address: parentAddress,
                  is_active: true
                })
                .select()
                .single();
              
              if (createError) {
                console.error('Error creating pickup location:', createError);
                alert(`Warning: Student was saved but failed to create pickup location: ${createError.message}`);
              } else {
                finalPickupLocationId = newLocation.id;
                console.log('Created new pickup location with parent address:', newLocation.id);
              }
            }
            
            // Update student with the pickup location ID
            if (finalPickupLocationId) {
              const updateResult = await supabase
                .from('students')
                .update({ pickup_location_id: finalPickupLocationId })
                .eq('id', data.id);
              
              if (updateResult.error) {
                console.error('Error updating student with pickup location:', updateResult.error);
              } else {
                // Update the data object for use in subsequent operations
                data.pickup_location_id = finalPickupLocationId;
              }
            }
          } catch (locationErr) {
            console.error('Exception handling pickup location:', locationErr);
            alert(`Warning: Student was saved but there was an error setting up pickup location: ${locationErr.message}`);
          }
        }
        
        // Save multiple parents to parents table and create relationships
        if (studentForm.parents && studentForm.parents.length > 0 && data?.id) {
          try {
            for (let i = 0; i < studentForm.parents.length; i++) {
              const parent = studentForm.parents[i];
              let parentId = null;
              let parentAuthUserId = null;
              
              if (parent.useExisting && parent.existingParentId) {
                // Use existing parent
                parentId = parent.existingParentId;
                console.log(`Using existing parent ${i + 1} with ID:`, parentId);
              } else {
                // Create new parent
                // First, check if parent already exists in parents table
                // Note: Same email can be used for teacher and parent (different user types)
                const { data: existingParent, error: checkError } = await supabase
                  .from('parents')
                  .select('id, auth_user_id')
                  .eq('school_id', schoolData.id)
                  .eq('email', parent.email.trim().toLowerCase())
                  .maybeSingle();
                
                if (checkError && checkError.code !== 'PGRST116') {
                  console.error(`Error checking existing parent ${i + 1}:`, checkError);
                }
                
                if (existingParent) {
                  // Parent already exists in parents table, use it
                  parentId = existingParent.id;
                  parentAuthUserId = existingParent.auth_user_id;
                  console.log(`Parent ${i + 1} already exists with ID:`, parentId);
                  
                  // Update parent info if needed
                  await supabase
                    .from('parents')
                    .update({
                      name: parent.name.trim(),
                      phone: parent.phone.trim() ? `${receptionistParentPhoneCountryCodes[i] || ''} ${parent.phone.trim()}`.trim() : null,
                      relationship: parent.relationship.trim() || null,
                      address: parent.address.trim() || null,
                      can_pickup: !!parent.canPickup,
                      is_emergency_contact: !!parent.isEmergencyContact,
                      pays_fees: !!parent.paysFees,
                      is_primary: parent.primaryContact === 'primary'
                    })
                    .eq('id', parentId);
                } else {
                  // Create auth user for new parent
                  // Note: If email already exists in auth.users (e.g., used for teacher), 
                  // we'll skip auth creation but still create the parent record
                  if (parent.password && parent.password.trim()) {
                    try {
                      const { data: parentAuthData, error: parentAuthError } = await supabase.auth.signUp({
                        email: parent.email.trim(),
                        password: parent.password.trim(),
                        options: {
                          data: {
                            first_name: parent.name.split(' ')[0] || parent.name,
                            last_name: parent.name.split(' ').slice(1).join(' ') || '',
                            user_type: 'parent',
                            child_email: studentForm.email,
                            school_id: schoolData.id
                          },
                          emailRedirectTo: undefined
                        }
                      });
                      
                      if (parentAuthData?.user?.id) {
                        parentAuthUserId = parentAuthData.user.id;
                        console.log(`Parent ${i + 1} auth user created:`, parentAuthUserId);
                        await supabase.auth.signOut();
                        
                        // Create user record (allow same email for different user types)
                        await supabase
                          .from('users')
                          .upsert({
                            id: parentAuthUserId,
                            email: parent.email.trim(),
                            first_name: parent.name.split(' ')[0] || parent.name,
                            last_name: parent.name.split(' ').slice(1).join(' ') || '',
                            user_type: 'parent'
                          }, { onConflict: 'id' });
                      } else if (parentAuthError) {
                        // If email already exists in auth.users (e.g., used for teacher), 
                        // that's okay - we'll continue without auth_user_id
                        if (parentAuthError.message && parentAuthError.message.includes('already registered')) {
                          console.log(`Parent ${i + 1} email already exists in auth.users (may be used for another user type), continuing without auth user`);
                        } else {
                          console.warn(`Parent ${i + 1} auth creation failed:`, parentAuthError.message);
                        }
                      }
                    } catch (authErr) {
                      // If email already exists, that's okay - continue without auth user
                      if (authErr.message && authErr.message.includes('already registered')) {
                        console.log(`Parent ${i + 1} email already exists in auth.users, continuing without auth user`);
                      } else {
                        console.error(`Parent ${i + 1} auth creation exception:`, authErr);
                      }
                    }
                  }
                  
                  // Insert new parent
                  const { data: newParent, error: parentError } = await supabase
                    .from('parents')
                    .insert({
                      school_id: schoolData.id,
                      name: parent.name.trim(),
                      email: parent.email.trim().toLowerCase(),
                      phone: parent.phone.trim() ? `${receptionistParentPhoneCountryCodes[i] || ''} ${parent.phone.trim()}`.trim() : null,
                      relationship: parent.relationship.trim() || null,
                      address: parent.address.trim() || null,
                      can_pickup: !!parent.canPickup,
                      is_emergency_contact: !!parent.isEmergencyContact,
                      pays_fees: !!parent.paysFees,
                      is_primary: parent.primaryContact === 'primary',
                      auth_user_id: parentAuthUserId
                    })
                    .select()
                    .single();
                  
                  if (parentError) {
                    console.error(`Error creating parent ${i + 1}:`, parentError);
                    alert(`Failed to save parent ${i + 1} (${parent.name}): ${parentError.message}`);
                  } else {
                    parentId = newParent.id;
                    console.log(`Parent ${i + 1} created with ID:`, parentId);
                  }
                }
              }
              
              // Create student-parent relationship
              if (parentId) {
                const { error: relationshipError } = await supabase
                  .from('student_parents')
                  .insert({
                    student_id: data.id,
                    parent_id: parentId,
                    is_primary: parent.primaryContact === 'primary'
                  });
                
                if (relationshipError) {
                  console.error(`Error creating relationship for parent ${i + 1}:`, relationshipError);
                  if (relationshipError.code !== '23505') { // Ignore duplicate key errors
                    alert(`Failed to link parent ${i + 1} (${parent.name}) to student: ${relationshipError.message}`);
                  }
                } else {
                  console.log(`Parent ${i + 1} linked to student successfully`);
                  
                  // Upload and save parent documents (National ID and Passport)
                  if (parent.documents && parent.documents.length > 0 && data?.id) {
                    try {
                      for (const doc of parent.documents) {
                        if (doc.file) {
                          try {
                            const fileExt = doc.file.name.split('.').pop();
                            const fileName = `${doc.type.replace(/\s+/g, '_')}_Parent${i + 1}_${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
                            const filePath = `student-documents/${schoolData.id}/${fileName}`;
                            
                            const { error: uploadError } = await supabase.storage
                              .from('student-images')
                              .upload(filePath, doc.file);
                            
                            if (uploadError) {
                              console.error(`Error uploading ${doc.type} for parent ${i + 1}:`, uploadError);
                              continue; // Continue with other documents
                            }
                            
                            // Get public URL
                            const { data: { publicUrl } } = supabase.storage
                              .from('student-images')
                              .getPublicUrl(filePath);
                            
                            // Save document record with parent_id
                            const { error: docError } = await supabase
                              .from('student_documents')
                              .insert({
                                student_id: data.id,
                                parent_id: parentId,
                                school_id: schoolData.id,
                                document_type: doc.type,
                                document_url: publicUrl,
                                file_name: doc.file.name,
                                file_size: doc.file.size
                              });
                            
                            if (docError) {
                              console.error(`Error saving ${doc.type} for parent ${i + 1}:`, docError);
                            } else {
                              console.log(`${doc.type} saved successfully for parent ${i + 1}`);
                            }
                          } catch (docUploadErr) {
                            console.error(`Exception uploading ${doc.type} for parent ${i + 1}:`, docUploadErr);
                          }
                        }
                      }
                    } catch (parentDocsErr) {
                      console.error(`Error saving documents for parent ${i + 1}:`, parentDocsErr);
                    }
                  }
                }
              }
            }
          } catch (parentsErr) {
            console.error('Error saving parents:', parentsErr);
            alert(`Warning: Student was saved but there was an error saving some parent information: ${parentsErr.message}`);
          }
        }
      }
      
      // Save documents to student_documents table
      // Only save documents that have files (new uploads), not existing parent documents
      const newDocumentsToSave = uploadedDocuments.filter(doc => doc.fileName && doc.url);
      if (newDocumentsToSave.length > 0 && data?.id) {
        try {
          const documentRecords = newDocumentsToSave.map(doc => ({
            student_id: data.id,
            school_id: schoolData.id,
            document_type: doc.type,
            document_url: doc.url,
            file_name: doc.fileName,
            file_size: doc.fileSize
          }));
          
          const { error: docError } = await supabase
            .from('student_documents')
            .insert(documentRecords);
          
          if (docError) {
            console.error('Error saving documents:', docError);
            // Don't fail the whole operation, just log the error
          } else {
            console.log('Documents saved successfully');
          }
        } catch (docErr) {
          console.error('Exception saving documents:', docErr);
        }
      }
      
      // For existing parent documents (documents with existingDocument flag),
      // create new records linking them to this new student
      const existingParentDocs = studentForm.documents.filter(doc => doc.existingDocument && doc.documentId);
      if (existingParentDocs.length > 0 && data?.id) {
        try {
          // Fetch the original documents to get their URLs
          const documentIds = existingParentDocs.map(doc => doc.documentId);
          const { data: originalDocs, error: fetchError } = await supabase
            .from('student_documents')
            .select('*')
            .in('id', documentIds);
          
          if (!fetchError && originalDocs) {
            const newDocumentRecords = originalDocs.map(originalDoc => {
              const matchingDoc = existingParentDocs.find(doc => doc.documentId === originalDoc.id);
              return {
                student_id: data.id,
                school_id: schoolData.id,
                document_type: matchingDoc?.type || originalDoc.document_type,
                document_url: originalDoc.document_url,
                file_name: originalDoc.file_name,
                file_size: originalDoc.file_size
              };
            });
            
            const { error: insertError } = await supabase
              .from('student_documents')
              .insert(newDocumentRecords);
            
            if (insertError) {
              console.error('Error saving existing parent documents:', insertError);
            } else {
              console.log('Existing parent documents linked successfully');
            }
          }
        } catch (err) {
          console.error('Exception saving existing parent documents:', err);
        }
      }
      
      // Check if this student has siblings (other students with same parent emails)
      let siblingsInfo = '';
      if (studentForm.parents && studentForm.parents.length > 0) {
        // Check for siblings using the first parent that used existing
        const existingParent = studentForm.parents.find(p => p.useExisting && p.existingParentId);
        if (existingParent) {
          try {
            // Get parent email from the parent record
            const { data: parentRecord } = await supabase
              .from('parents')
              .select('email')
              .eq('id', existingParent.existingParentId)
              .single();
            
            if (parentRecord?.email) {
              const { data: siblings } = await supabase
                .from('students')
                .select('id, first_name, last_name, class_id, section_id')
                .eq('school_id', schoolData.id)
                .eq('parent_email', parentRecord.email.trim())
                .eq('is_active', true)
                .neq('id', data.id); // Exclude the newly created student
              
              if (siblings && siblings.length > 0) {
                siblingsInfo = `\n\nThis student is now a sibling of ${siblings.length} other student(s) with the same parent.`;
              }
            }
          } catch (err) {
            console.warn('Could not fetch sibling information:', err);
          }
        }
      }
      
      // Reset form
      setStudentForm({
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        dateOfBirth: '',
        ageYears: '',
        gender: '',
        nationality: 'Kenyan',
        phone: '',
        classId: '',
        sectionId: '',
        admissionDate: '',
        previousSchool: '',
        previousSchoolGrade: '',
        studentIdNo: '',
        parents: [
          {
            name: '',
            relationship: '',
            primaryContact: 'primary',
            email: '',
            password: '',
            confirmPassword: '',
            phone: '',
            address: '',
            canPickup: false,
            isEmergencyContact: false,
            paysFees: false,
            useExisting: false,
            existingParentId: null
          }
        ],
        address: '',
        emergencyContact: '',
        emergencyPhone: '',
        profilePicture: null,
        // Medical Details
        medicalDetails: '',
        emergencyDocuments: null,
        // Transport Service
        transportServiceEnabled: false,
        pickupLocationId: '',
        useStudentAddressAsPickup: false,
        transportRouteType: '',
        transportTime: '',
        routeId: '',
        // Fees
        paymentMethod: '',
        // Documents
        documents: []
      });
      setSections([]);
      setFormErrors({});
      
      // Reset parent selection - Default to Kenya (+254)
      setReceptionistParentPhoneCountryCodes(['+254']);
      setReceptionistStudentPhoneCountryCode('+254');
      setReceptionistEmergencyPhoneCountryCode('+254');
      setShowParentPasswords([false]);
      setShowParentConfirmPasswords([false]);
      setParentSearchTerms(['']);
      setExistingParents([]);
      
      // Show beautiful success popup
      const studentFullName = `${studentForm.firstName} ${studentForm.middleName ? studentForm.middleName + ' ' : ''}${studentForm.lastName}`.trim();
      setAddedStudentName(studentFullName);
      setShowSuccessPopup(true);
      
      // If in edit mode, call onCancel after a short delay to close the form
      if (isEditMode && onCancel) {
        setTimeout(() => {
          onCancel();
        }, 2000); // Close after 2 seconds
      }
      
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to add student. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">⏳</div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="add-student-view">
      <div className="content-header">
        <h1>{isEditMode ? 'Edit Student' : 'Add New Student'}</h1>
        <p>{isEditMode ? 'Update the student details below.' : 'Fill in the student details below. The student will require school approval before appearing in the system.'}</p>
        {isEditMode && onCancel && (
          <button 
            onClick={onCancel}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              background: '#F3F4F6',
              color: '#374151',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Cancel
          </button>
        )}
      </div>
        
        <div className="content-body">
          <div className="dashboard-content">
            <div className="add-student-section">
              <div className="add-student-card">
                <form onSubmit={handleSubmit} className="add-student-form">
                  {submissionError && (
                    <div className="submission-error-summary" role="alert" style={{
                      padding: '1rem',
                      marginBottom: '1.5rem',
                      backgroundColor: '#FEE2E2',
                      border: '2px solid #EF4444',
                      borderRadius: '8px',
                      color: '#991B1B'
                    }}>
                      <div style={{ fontWeight: '600', marginBottom: '0.5rem', fontSize: '1rem' }}>
                        ⚠️ Form Validation Errors
                      </div>
                      <div style={{ whiteSpace: 'pre-line', fontSize: '0.95rem' }}>
                        {submissionError}
                      </div>
                    </div>
                  )}
                  <div className="form-section">
                    <h3>A) Student Information</h3>
                    {/* Show branch selection when branches exist */}
                    {branches.length > 0 && (
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="formBranchId">Branch *</label>
                          <select
                            id="formBranchId"
                            name="formBranchId"
                            value={formBranchId || ''}
                            onChange={(e) => setFormBranchId(e.target.value ? parseInt(e.target.value) : null)}
                            className={formErrors.branchId ? 'error' : ''}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              fontSize: '1rem',
                              border: formErrors.branchId ? '2px solid #EF4444' : '1px solid #D1D5DB',
                              borderRadius: '6px',
                              backgroundColor: '#FFFFFF'
                            }}
                          >
                            <option value="">Select Branch</option>
                            {branches.map((branch) => (
                              <option key={branch.id} value={branch.id}>
                                {branch.branch_name}
                              </option>
                            ))}
                          </select>
                          {formErrors.branchId && (
                            <span className="error-text" style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                              {formErrors.branchId}
                            </span>
                          )}
                          <small className="form-help" style={{ display: 'block', marginTop: '0.5rem', color: '#6B7280', fontSize: '0.75rem' }}>
                            Please select the branch where this student will be enrolled.
                          </small>
                        </div>
                      </div>
                    )}
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="firstName">First Name *</label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={studentForm.firstName}
                          onChange={handleInputChange}
                          className={formErrors.firstName ? 'error' : ''}
                          placeholder="Enter first name"
                        />
                        {formErrors.firstName && (
                          <span className="error-text">{formErrors.firstName}</span>
                        )}
                      </div>
                      
                    <div className="form-group">
                      <label htmlFor="middleName">Middle Name</label>
                      <input
                        type="text"
                        id="middleName"
                        name="middleName"
                        value={studentForm.middleName}
                        onChange={handleInputChange}
                        placeholder="Enter middle name (optional)"
                      />
                    </div>

                      <div className="form-group">
                        <label htmlFor="lastName">Last Name *</label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={studentForm.lastName}
                          onChange={handleInputChange}
                          className={formErrors.lastName ? 'error' : ''}
                          placeholder="Enter last name"
                        />
                        {formErrors.lastName && (
                          <span className="error-text">{formErrors.lastName}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="email">Email *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={studentForm.email}
                          onChange={handleInputChange}
                          className={formErrors.email ? 'error' : ''}
                          placeholder="student@example.com"
                        />
                        {formErrors.email && (
                          <span className="error-text">{formErrors.email}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="dateOfBirth">Date of Birth *</label>
                        <input
                          type="date"
                          id="dateOfBirth"
                          name="dateOfBirth"
                          value={studentForm.dateOfBirth}
                          onChange={handleInputChange}
                          className={formErrors.dateOfBirth ? 'error' : ''}
                          max={(() => {
                            const today = new Date();
                            const maxDate = new Date(today.getFullYear() - 2, today.getMonth(), today.getDate());
                            return maxDate.toISOString().split('T')[0];
                          })()}
                          min={(() => {
                            const today = new Date();
                            const minDate = new Date(today.getFullYear() - 100, today.getMonth(), today.getDate());
                            return minDate.toISOString().split('T')[0];
                          })()}
                        />
                        {formErrors.dateOfBirth && (
                          <span className="error-text">{formErrors.dateOfBirth}</span>
                        )}
                      {studentForm.ageYears && !formErrors.dateOfBirth && (
                        <small className="form-help">Age: {studentForm.ageYears} years</small>
                      )}
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="gender">Gender *</label>
                        <select
                          id="gender"
                          name="gender"
                          value={studentForm.gender}
                          onChange={handleInputChange}
                          className={formErrors.gender ? 'error' : ''}
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                        {formErrors.gender && (
                          <span className="error-text">{formErrors.gender}</span>
                        )}
                      </div>
                      
                    <div className="form-group">
                      <label htmlFor="nationality">Nationality *</label>
                      <select
                        id="nationality"
                        name="nationality"
                        value={studentForm.nationality}
                        onChange={handleInputChange}
                        className={formErrors.nationality ? 'error' : ''}
                      >
                        <option value="">Select Nationality</option>
                        <option value="Afghan">Afghan</option>
                        <option value="Albanian">Albanian</option>
                        <option value="Algerian">Algerian</option>
                        <option value="Argentine">Argentine</option>
                        <option value="Australian">Australian</option>
                        <option value="Austrian">Austrian</option>
                        <option value="Bangladeshi">Bangladeshi</option>
                        <option value="Belgian">Belgian</option>
                        <option value="Brazilian">Brazilian</option>
                        <option value="British">British</option>
                        <option value="Bulgarian">Bulgarian</option>
                        <option value="Canadian">Canadian</option>
                        <option value="Chinese">Chinese</option>
                        <option value="Colombian">Colombian</option>
                        <option value="Croatian">Croatian</option>
                        <option value="Czech">Czech</option>
                        <option value="Danish">Danish</option>
                        <option value="Dutch">Dutch</option>
                        <option value="Egyptian">Egyptian</option>
                        <option value="Filipino">Filipino</option>
                        <option value="Finnish">Finnish</option>
                        <option value="French">French</option>
                        <option value="German">German</option>
                        <option value="Greek">Greek</option>
                        <option value="Hungarian">Hungarian</option>
                        <option value="Indian">Indian</option>
                        <option value="Indonesian">Indonesian</option>
                        <option value="Iranian">Iranian</option>
                        <option value="Iraqi">Iraqi</option>
                        <option value="Irish">Irish</option>
                        <option value="Israeli">Israeli</option>
                        <option value="Italian">Italian</option>
                        <option value="Japanese">Japanese</option>
                        <option value="Jordanian">Jordanian</option>
                        <option value="Kenyan">Kenyan</option>
                        <option value="Korean">Korean</option>
                        <option value="Kuwaiti">Kuwaiti</option>
                        <option value="Lebanese">Lebanese</option>
                        <option value="Malaysian">Malaysian</option>
                        <option value="Mexican">Mexican</option>
                        <option value="Moroccan">Moroccan</option>
                        <option value="Nepalese">Nepalese</option>
                        <option value="New Zealander">New Zealander</option>
                        <option value="Nigerian">Nigerian</option>
                        <option value="Norwegian">Norwegian</option>
                        <option value="Omani">Omani</option>
                        <option value="Pakistani">Pakistani</option>
                        <option value="Palestinian">Palestinian</option>
                        <option value="Peruvian">Peruvian</option>
                        <option value="Polish">Polish</option>
                        <option value="Portuguese">Portuguese</option>
                        <option value="Qatari">Qatari</option>
                        <option value="Romanian">Romanian</option>
                        <option value="Russian">Russian</option>
                        <option value="Saudi Arabian">Saudi Arabian</option>
                        <option value="Singaporean">Singaporean</option>
                        <option value="South African">South African</option>
                        <option value="Spanish">Spanish</option>
                        <option value="Sri Lankan">Sri Lankan</option>
                        <option value="Swedish">Swedish</option>
                        <option value="Swiss">Swiss</option>
                        <option value="Syrian">Syrian</option>
                        <option value="Thai">Thai</option>
                        <option value="Turkish">Turkish</option>
                        <option value="Ukrainian">Ukrainian</option>
                        <option value="Emirati">Emirati (UAE)</option>
                        <option value="American">American (USA)</option>
                        <option value="Venezuelan">Venezuelan</option>
                        <option value="Vietnamese">Vietnamese</option>
                        <option value="Yemeni">Yemeni</option>
                        <option value="Other">Other</option>
                      </select>
                      {formErrors.nationality && (
                        <span className="error-text">{formErrors.nationality}</span>
                      )}
                    </div>

                      <div className="form-group">
                        <label htmlFor="admissionDate">Admission Date *</label>
                        <input
                          type="date"
                          id="admissionDate"
                          name="admissionDate"
                          value={studentForm.admissionDate}
                          onChange={handleInputChange}
                          className={formErrors.admissionDate ? 'error' : ''}
                        />
                        {formErrors.admissionDate && (
                          <span className="error-text">{formErrors.admissionDate}</span>
                        )}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="phone">Student Phone *</label>
                        <div className="phone-input-wrapper" style={{ display: 'flex', gap: '8px' }}>
                          <select
                            className="country-code-select"
                            value={receptionistStudentPhoneCountryCode}
                            onChange={(e) => setReceptionistStudentPhoneCountryCode(e.target.value)}
                            style={{ width: '50%', minWidth: '150px', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '0.95rem', flexShrink: 0 }}
                          >
                            <option value="">Select Country</option>
                            <option value="+1">🇺🇸 +1 (US/CA)</option>
                            <option value="+44">🇬🇧 +44 (UK)</option>
                            <option value="+91">🇮🇳 +91 (India)</option>
                            <option value="+92">🇵🇰 +92 (Pakistan)</option>
                            <option value="+971">🇦🇪 +971 (UAE)</option>
                            <option value="+966">🇸🇦 +966 (Saudi Arabia)</option>
                            <option value="+974">🇶🇦 +974 (Qatar)</option>
                            <option value="+965">🇰🇼 +965 (Kuwait)</option>
                            <option value="+973">🇧🇭 +973 (Bahrain)</option>
                            <option value="+968">🇴🇲 +968 (Oman)</option>
                            <option value="+961">🇱🇧 +961 (Lebanon)</option>
                            <option value="+962">🇯🇴 +962 (Jordan)</option>
                            <option value="+20">🇪🇬 +20 (Egypt)</option>
                            <option value="+27">🇿🇦 +27 (South Africa)</option>
                            <option value="+61">🇦🇺 +61 (Australia)</option>
                            <option value="+64">🇳🇿 +64 (New Zealand)</option>
                            <option value="+86">🇨🇳 +86 (China)</option>
                            <option value="+81">🇯🇵 +81 (Japan)</option>
                            <option value="+82">🇰🇷 +82 (South Korea)</option>
                            <option value="+65">🇸🇬 +65 (Singapore)</option>
                            <option value="+60">🇲🇾 +60 (Malaysia)</option>
                            <option value="+66">🇹🇭 +66 (Thailand)</option>
                            <option value="+62">🇮🇩 +62 (Indonesia)</option>
                            <option value="+63">🇵🇭 +63 (Philippines)</option>
                            <option value="+84">🇻🇳 +84 (Vietnam)</option>
                            <option value="+880">🇧🇩 +880 (Bangladesh)</option>
                            <option value="+94">🇱🇰 +94 (Sri Lanka)</option>
                            <option value="+977">🇳🇵 +977 (Nepal)</option>
                            <option value="+33">🇫🇷 +33 (France)</option>
                            <option value="+49">🇩🇪 +49 (Germany)</option>
                            <option value="+39">🇮🇹 +39 (Italy)</option>
                            <option value="+34">🇪🇸 +34 (Spain)</option>
                            <option value="+31">🇳🇱 +31 (Netherlands)</option>
                            <option value="+32">🇧🇪 +32 (Belgium)</option>
                            <option value="+41">🇨🇭 +41 (Switzerland)</option>
                            <option value="+43">🇦🇹 +43 (Austria)</option>
                            <option value="+46">🇸🇪 +46 (Sweden)</option>
                            <option value="+47">🇳🇴 +47 (Norway)</option>
                            <option value="+45">🇩🇰 +45 (Denmark)</option>
                            <option value="+358">🇫🇮 +358 (Finland)</option>
                            <option value="+7">🇷🇺 +7 (Russia)</option>
                            <option value="+90">🇹🇷 +90 (Turkey)</option>
                            <option value="+52">🇲🇽 +52 (Mexico)</option>
                            <option value="+55">🇧🇷 +55 (Brazil)</option>
                            <option value="+54">🇦🇷 +54 (Argentina)</option>
                            <option value="+57">🇨🇴 +57 (Colombia)</option>
                            <option value="+234">🇳🇬 +234 (Nigeria)</option>
                            <option value="+254">🇰🇪 +254 (Kenya)</option>
                            <option value="+212">🇲🇦 +212 (Morocco)</option>
                            <option value="+972">🇮🇱 +972 (Israel)</option>
                            <option value="+98">🇮🇷 +98 (Iran)</option>
                            <option value="+964">🇮🇶 +964 (Iraq)</option>
                            <option value="+967">🇾🇪 +967 (Yemen)</option>
                            <option value="+963">🇸🇾 +963 (Syria)</option>
                          </select>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={studentForm.phone}
                            onChange={handleInputChange}
                            className={formErrors.phone ? 'error' : ''}
                            placeholder="e.g. 300 1234567"
                            style={{ width: '50%', flex: 1, minWidth: 0 }}
                          />
                        </div>
                        {formErrors.phone && (
                          <span className="error-text">{formErrors.phone}</span>
                        )}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="previousSchool">Previous School (optional)</label>
                        <input
                          type="text"
                          id="previousSchool"
                          name="previousSchool"
                          value={studentForm.previousSchool}
                          onChange={handleInputChange}
                          placeholder="Enter previous school (if applicable)"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="previousSchoolGrade">Grade/Percentage from Previous School (optional)</label>
                        <input
                          type="text"
                          id="previousSchoolGrade"
                          name="previousSchoolGrade"
                          value={studentForm.previousSchoolGrade}
                          onChange={handleInputChange}
                          placeholder="e.g., A+, 95%, Grade 8, etc."
                        />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="profilePicture">Profile Picture</label>
                        <input
                          type="file"
                          id="profilePicture"
                          name="profilePicture"
                          onChange={handleFileChange}
                          accept="image/*"
                          className={formErrors.profilePicture ? 'error' : ''}
                        />
                        {formErrors.profilePicture && (
                          <span className="error-text">{formErrors.profilePicture}</span>
                        )}
                        <small className="form-help">
                          Upload a profile picture (JPG, PNG, GIF - Max 5MB)
                        </small>
                      </div>
                    </div>

                    {/* Student Address Field */}
                    <div className="form-row">
                      <div className="form-group full-width">
                        <label htmlFor="studentAddress" style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '500', color: '#374151' }}>
                          Student Address *
                        </label>
                        <input
                          type="text"
                          id="studentAddress"
                          name="address"
                          value={studentForm.address}
                          onChange={handleInputChange}
                          className={formErrors.address ? 'error' : ''}
                          placeholder="Enter student address"
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            borderRadius: '6px',
                            border: formErrors.address ? '1px solid #EF4444' : '1px solid #D1D5DB',
                            fontSize: '1rem',
                            backgroundColor: '#FFFFFF',
                            color: '#374151',
                            outline: 'none',
                            transition: 'border-color 0.2s',
                            boxSizing: 'border-box'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#FF8C42'}
                          onBlur={(e) => e.target.style.borderColor = formErrors.address ? '#EF4444' : '#D1D5DB'}
                        />
                        {formErrors.address && (
                          <span className="error-text" style={{ display: 'block', marginTop: '0.5rem' }}>{formErrors.address}</span>
                        )}
                        {formErrors.selectedParentAddressIndex && (
                          <span className="error-text" style={{ display: 'block', marginTop: '0.5rem' }}>{formErrors.selectedParentAddressIndex}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <h3>Student Credentials</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="password">Password *</label>
                        <div className="password-input-wrapper" style={{ position: 'relative' }}>
                          <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            name="password"
                            value={studentForm.password}
                            onChange={handleInputChange}
                            className={formErrors.password ? 'error' : ''}
                            placeholder="Enter password"
                            style={{ paddingRight: '2.5rem' }}
                          />
                          <span 
                            className="password-toggle-icon"
                            onClick={() => setShowPassword(!showPassword)}
                            title={showPassword ? "Hide password" : "Show password"}
                            style={{
                              position: 'absolute',
                              right: '0.75rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              cursor: 'pointer',
                              fontSize: '1.2rem',
                              userSelect: 'none',
                              zIndex: 1
                            }}
                          >
                            {showPassword ? '🙈' : '👁️'}
                          </span>
                        </div>
                        {formErrors.password && (
                          <span className="error-text">{formErrors.password}</span>
                        )}
                        <div style={{ marginTop: '0.5rem' }}>
                          <button
                            type="button"
                            className="toggle-btn"
                            onClick={() => {
                              const genPassword = generatePassword();
                              setStudentForm(prev => ({
                                ...prev,
                                password: genPassword,
                                confirmPassword: genPassword
                              }));
                            }}
                            style={{
                              padding: '0.4rem 0.8rem',
                              backgroundColor: '#10B981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              fontSize: '0.85rem'
                            }}
                          >
                            Generate Password
                          </button>
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm Password *</label>
                        <div className="password-input-wrapper" style={{ position: 'relative' }}>
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            id="confirmPassword"
                            name="confirmPassword"
                            value={studentForm.confirmPassword}
                            onChange={handleInputChange}
                            className={formErrors.confirmPassword ? 'error' : ''}
                            placeholder="Confirm password"
                            style={{ paddingRight: '2.5rem' }}
                          />
                          <span 
                            className="password-toggle-icon"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            title={showConfirmPassword ? "Hide password" : "Show password"}
                            style={{
                              position: 'absolute',
                              right: '0.75rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              cursor: 'pointer',
                              fontSize: '1.2rem',
                              userSelect: 'none',
                              zIndex: 1
                            }}
                          >
                            {showConfirmPassword ? '🙈' : '👁️'}
                          </span>
                        </div>
                        {formErrors.confirmPassword && (
                          <span className="error-text">{formErrors.confirmPassword}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <h3 style={{ marginBottom: '1.5rem' }}>B) Parent/Guardian Information</h3>
                    
                    {studentForm.parents.map((parent, parentIndex) => (
                      <div key={parentIndex} style={{ 
                        marginBottom: '2rem', 
                        padding: '1.5rem', 
                        border: '1px solid #E5E7EB', 
                        borderRadius: '12px',
                        backgroundColor: '#FAFAFA',
                        position: 'relative'
                      }}>
                        {studentForm.parents.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeParent(parentIndex)}
                            style={{
                              position: 'absolute',
                              top: '1rem',
                              right: '1rem',
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#EF4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              width: 'fit-content',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Remove Parent
                          </button>
                        )}
                        
                        <h4 style={{ marginBottom: '1rem', color: '#1F2937' }}>
                          Parent {parentIndex + 1}
                        </h4>
                        
                        {/* Parent Selection Toggle */}
                        <div className="form-row">
                          <div className="form-group">
                            <label>Parent Selection</label>
                            <div className="parent-selection-toggle">
                              <button
                                type="button"
                                className={`toggle-btn ${!parent.useExisting ? 'active' : ''}`}
                                onClick={() => handleParentSelectionToggle(parentIndex, false)}
                              >
                                Add New Parent
                              </button>
                              <button
                                type="button"
                                className={`toggle-btn ${parent.useExisting ? 'active' : ''}`}
                                onClick={() => handleParentSelectionToggle(parentIndex, true)}
                              >
                                Select Existing Parent
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Existing Parent Selection */}
                        {parent.useExisting && (
                          <div className="form-row">
                            <div className="form-group">
                              <label htmlFor={`parentSearch-${parentIndex}`}>Search Existing Parents *</label>
                              <div className="parent-search-container">
                                <input
                                  type="text"
                                  id={`parentSearch-${parentIndex}`}
                                  value={parentSearchTerms[parentIndex] || ''}
                                  onChange={(e) => handleParentSearch(parentIndex, e.target.value)}
                                  placeholder="Search by name or email..."
                                  className={formErrors[`parents.${parentIndex}.selection`] ? 'error' : ''}
                                  disabled={parent.existingParentId ? true : false}
                                />
                                {parent.existingParentId && (
                                  <button
                                    type="button"
                                    className="clear-selection-btn"
                                    onClick={() => handleClearParentSelection(parentIndex)}
                                    title="Clear selection"
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                              {formErrors[`parents.${parentIndex}.selection`] && (
                                <span className="error-text">{formErrors[`parents.${parentIndex}.selection`]}</span>
                              )}
                              
                              {/* Parent Dropdown */}
                              {parentSearchTerms[parentIndex] && existingParents.length > 0 && !parent.existingParentId && (
                                <div className="parent-dropdown">
                                  {existingParents.map((existingParent) => (
                                    <div
                                      key={existingParent.id}
                                      className="parent-option"
                                      onClick={() => handleParentSelect(parentIndex, existingParent)}
                                    >
                                      <div className="parent-name">{existingParent.name}</div>
                                      <div className="parent-email">{existingParent.email}</div>
                                      {existingParent.phone && (
                                        <div className="parent-phone">{existingParent.phone}</div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {loadingParents && (
                                <div className="loading-text">Loading parents...</div>
                              )}
                              
                              {parentSearchTerms[parentIndex] && existingParents.length === 0 && !loadingParents && !parent.existingParentId && (
                                <div className="no-parents-text">No parents found matching your search.</div>
                              )}
                              
                              {parent.existingParentId && (
                                <div className="selected-parent-info">
                                  <div className="selected-parent-label">Selected Parent:</div>
                                  <div className="selected-parent-details">
                                    <strong>{parent.name}</strong> ({parent.email})
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Parent Details Form */}
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor={`parentName-${parentIndex}`}>Parent Name *</label>
                            <input
                              type="text"
                              id={`parentName-${parentIndex}`}
                              value={parent.name}
                              onChange={(e) => handleParentInputChange(parentIndex, 'name', e.target.value)}
                              className={formErrors[`parents.${parentIndex}.name`] ? 'error' : ''}
                              placeholder="Enter parent/guardian name"
                              disabled={parent.useExisting}
                            />
                            {formErrors[`parents.${parentIndex}.name`] && (
                              <span className="error-text">{formErrors[`parents.${parentIndex}.name`]}</span>
                            )}
                          </div>
                          
                          <div className="form-group">
                            <label htmlFor={`parentEmail-${parentIndex}`}>Parent Email *</label>
                            <input
                              type="email"
                              id={`parentEmail-${parentIndex}`}
                              value={parent.email}
                              onChange={(e) => handleParentInputChange(parentIndex, 'email', e.target.value)}
                              className={formErrors[`parents.${parentIndex}.email`] ? 'error' : ''}
                              placeholder="parent@example.com"
                              disabled={parent.useExisting}
                            />
                            {formErrors[`parents.${parentIndex}.email`] && (
                              <span className="error-text">{formErrors[`parents.${parentIndex}.email`]}</span>
                            )}
                          </div>
                        </div>
                        
                        {/* Password fields only for new parents */}
                        {!parent.useExisting && (
                          <div className="form-row">
                            <div className="form-group">
                              <label htmlFor={`parentPassword-${parentIndex}`}>Parent Password *</label>
                              <div className="password-input-wrapper" style={{ position: 'relative' }}>
                                <input
                                  type={showParentPasswords[parentIndex] ? "text" : "password"}
                                  id={`parentPassword-${parentIndex}`}
                                  value={parent.password}
                                  onChange={(e) => handleParentInputChange(parentIndex, 'password', e.target.value)}
                                  className={formErrors[`parents.${parentIndex}.password`] ? 'error' : ''}
                                  placeholder="Enter password"
                                  style={{ paddingRight: '2.5rem' }}
                                />
                                <span 
                                  className="password-toggle-icon"
                                  onClick={() => {
                                    const newShow = [...showParentPasswords];
                                    newShow[parentIndex] = !newShow[parentIndex];
                                    setShowParentPasswords(newShow);
                                  }}
                                  title={showParentPasswords[parentIndex] ? "Hide password" : "Show password"}
                                  style={{
                                    position: 'absolute',
                                    right: '0.75rem',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    cursor: 'pointer',
                                    fontSize: '1.2rem',
                                    userSelect: 'none',
                                    zIndex: 1
                                  }}
                                >
                                  {showParentPasswords[parentIndex] ? '🙈' : '👁️'}
                                </span>
                              </div>
                              {formErrors[`parents.${parentIndex}.password`] && (
                                <span className="error-text">{formErrors[`parents.${parentIndex}.password`]}</span>
                              )}
                            </div>
                            
                            <div className="form-group">
                              <label htmlFor={`parentConfirmPassword-${parentIndex}`}>Confirm Parent Password *</label>
                              <div className="password-input-wrapper" style={{ position: 'relative' }}>
                                <input
                                  type={showParentConfirmPasswords[parentIndex] ? "text" : "password"}
                                  id={`parentConfirmPassword-${parentIndex}`}
                                  value={parent.confirmPassword}
                                  onChange={(e) => handleParentInputChange(parentIndex, 'confirmPassword', e.target.value)}
                                  className={formErrors[`parents.${parentIndex}.confirmPassword`] ? 'error' : ''}
                                  placeholder="Confirm parent password"
                                  style={{ paddingRight: '2.5rem' }}
                                />
                                <span 
                                  className="password-toggle-icon"
                                  onClick={() => {
                                    const newShow = [...showParentConfirmPasswords];
                                    newShow[parentIndex] = !newShow[parentIndex];
                                    setShowParentConfirmPasswords(newShow);
                                  }}
                                  title={showParentConfirmPasswords[parentIndex] ? "Hide password" : "Show password"}
                                  style={{
                                    position: 'absolute',
                                    right: '0.75rem',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    cursor: 'pointer',
                                    fontSize: '1.2rem',
                                    userSelect: 'none',
                                    zIndex: 1
                                  }}
                                >
                                  {showParentConfirmPasswords[parentIndex] ? '🙈' : '👁️'}
                                </span>
                              </div>
                              {formErrors[`parents.${parentIndex}.confirmPassword`] && (
                                <span className="error-text">{formErrors[`parents.${parentIndex}.confirmPassword`]}</span>
                              )}
                              <div style={{ marginTop: '0.5rem' }}>
                                <button
                                  type="button"
                                  className="toggle-btn"
                                  onClick={() => {
                                    const genPassword = generatePassword();
                                    handleParentInputChange(parentIndex, 'password', genPassword);
                                    handleParentInputChange(parentIndex, 'confirmPassword', genPassword);
                                  }}
                                  style={{
                                    padding: '0.4rem 0.8rem',
                                    backgroundColor: '#10B981',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    fontSize: '0.85rem'
                                  }}
                                >
                                  Generate Password
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor={`parentPhone-${parentIndex}`}>Parent Phone *</label>
                            <div className="phone-input-wrapper">
                              <select
                                className="country-code-select"
                                value={receptionistParentPhoneCountryCodes[parentIndex] || ''}
                                onChange={(e) => {
                                  const newCodes = [...receptionistParentPhoneCountryCodes];
                                  newCodes[parentIndex] = e.target.value;
                                  setReceptionistParentPhoneCountryCodes(newCodes);
                                }}
                                disabled={parent.useExisting}
                              >
                            <option value="+1">🇺🇸 +1 (US/CA)</option>
                            <option value="+44">🇬🇧 +44 (UK)</option>
                            <option value="+91">🇮🇳 +91 (India)</option>
                            <option value="+92">🇵🇰 +92 (Pakistan)</option>
                            <option value="+971">🇦🇪 +971 (UAE)</option>
                            <option value="+966">🇸🇦 +966 (Saudi Arabia)</option>
                            <option value="+974">🇶🇦 +974 (Qatar)</option>
                            <option value="+965">🇰🇼 +965 (Kuwait)</option>
                            <option value="+973">🇧🇭 +973 (Bahrain)</option>
                            <option value="+968">🇴🇲 +968 (Oman)</option>
                            <option value="+961">🇱🇧 +961 (Lebanon)</option>
                            <option value="+962">🇯🇴 +962 (Jordan)</option>
                            <option value="+20">🇪🇬 +20 (Egypt)</option>
                            <option value="+27">🇿🇦 +27 (South Africa)</option>
                            <option value="+61">🇦🇺 +61 (Australia)</option>
                            <option value="+64">🇳🇿 +64 (New Zealand)</option>
                            <option value="+86">🇨🇳 +86 (China)</option>
                            <option value="+81">🇯🇵 +81 (Japan)</option>
                            <option value="+82">🇰🇷 +82 (South Korea)</option>
                            <option value="+65">🇸🇬 +65 (Singapore)</option>
                            <option value="+60">🇲🇾 +60 (Malaysia)</option>
                            <option value="+66">🇹🇭 +66 (Thailand)</option>
                            <option value="+62">🇮🇩 +62 (Indonesia)</option>
                            <option value="+63">🇵🇭 +63 (Philippines)</option>
                            <option value="+84">🇻🇳 +84 (Vietnam)</option>
                            <option value="+880">🇧🇩 +880 (Bangladesh)</option>
                            <option value="+94">🇱🇰 +94 (Sri Lanka)</option>
                            <option value="+977">🇳🇵 +977 (Nepal)</option>
                            <option value="+33">🇫🇷 +33 (France)</option>
                            <option value="+49">🇩🇪 +49 (Germany)</option>
                            <option value="+39">🇮🇹 +39 (Italy)</option>
                            <option value="+34">🇪🇸 +34 (Spain)</option>
                            <option value="+31">🇳🇱 +31 (Netherlands)</option>
                            <option value="+32">🇧🇪 +32 (Belgium)</option>
                            <option value="+41">🇨🇭 +41 (Switzerland)</option>
                            <option value="+43">🇦🇹 +43 (Austria)</option>
                            <option value="+46">🇸🇪 +46 (Sweden)</option>
                            <option value="+47">🇳🇴 +47 (Norway)</option>
                            <option value="+45">🇩🇰 +45 (Denmark)</option>
                            <option value="+358">🇫🇮 +358 (Finland)</option>
                            <option value="+7">🇷🇺 +7 (Russia)</option>
                            <option value="+90">🇹🇷 +90 (Turkey)</option>
                            <option value="+52">🇲🇽 +52 (Mexico)</option>
                            <option value="+55">🇧🇷 +55 (Brazil)</option>
                            <option value="+54">🇦🇷 +54 (Argentina)</option>
                            <option value="+57">🇨🇴 +57 (Colombia)</option>
                            <option value="+234">🇳🇬 +234 (Nigeria)</option>
                            <option value="+254">🇰🇪 +254 (Kenya)</option>
                            <option value="+212">🇲🇦 +212 (Morocco)</option>
                            <option value="+972">🇮🇱 +972 (Israel)</option>
                            <option value="+98">🇮🇷 +98 (Iran)</option>
                            <option value="+964">🇮🇶 +964 (Iraq)</option>
                            <option value="+967">🇾🇪 +967 (Yemen)</option>
                            <option value="+963">🇸🇾 +963 (Syria)</option>
                          </select>
                          <input
                            type="tel"
                            id={`parentPhone-${parentIndex}`}
                            value={parent.phone}
                            onChange={(e) => handleParentInputChange(parentIndex, 'phone', e.target.value)}
                            className={formErrors[`parents.${parentIndex}.phone`] ? 'error' : ''}
                            placeholder="e.g. 300 1234567"
                            disabled={parent.useExisting}
                          />
                        </div>
                        {formErrors[`parents.${parentIndex}.phone`] && (
                          <span className="error-text">{formErrors[`parents.${parentIndex}.phone`]}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Relationship *</label>
                        <div className="parent-selection-toggle">
                          <button
                            type="button"
                            className={`toggle-btn ${parent.relationship === 'Mother' ? 'active' : ''}`}
                            onClick={() => handleParentInputChange(parentIndex, 'relationship', 'Mother')}
                            disabled={parent.useExisting}
                          >
                            Mother
                          </button>
                          <button
                            type="button"
                            className={`toggle-btn ${parent.relationship === 'Father' ? 'active' : ''}`}
                            onClick={() => handleParentInputChange(parentIndex, 'relationship', 'Father')}
                            disabled={parent.useExisting}
                          >
                            Father
                          </button>
                          <button
                            type="button"
                            className={`toggle-btn ${parent.relationship === 'Guardian' ? 'active' : ''}`}
                            onClick={() => handleParentInputChange(parentIndex, 'relationship', 'Guardian')}
                            disabled={parent.useExisting}
                          >
                            Guardian
                          </button>
                        </div>
                        {formErrors[`parents.${parentIndex}.relationship`] && (
                          <span className="error-text">{formErrors[`parents.${parentIndex}.relationship`]}</span>
                        )}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor={`parentPrimaryContact-${parentIndex}`}>Contact Priority</label>
                        <select
                          id={`parentPrimaryContact-${parentIndex}`}
                          value={parent.primaryContact}
                          onChange={(e) => handleParentInputChange(parentIndex, 'primaryContact', e.target.value)}
                          disabled={parent.useExisting}
                        >
                          <option value="primary">Primary</option>
                          <option value="secondary">Secondary</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor={`parentAddress-${parentIndex}`} style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                          Parent Address *
                        </label>
                        <input
                          type="text"
                          id={`parentAddress-${parentIndex}`}
                          value={studentForm.useParentAddress && studentForm.selectedParentAddressIndex === parentIndex.toString() 
                            ? studentForm.address 
                            : parent.address}
                          onChange={(e) => {
                            if (studentForm.useParentAddress && studentForm.selectedParentAddressIndex === parentIndex.toString()) {
                              // If checkbox is checked, update student address which will sync to parent
                              setStudentForm(prev => ({
                                ...prev,
                                address: e.target.value
                              }));
                            } else {
                              // Normal parent address update
                              handleParentInputChange(parentIndex, 'address', e.target.value);
                            }
                          }}
                          className={formErrors[`parents.${parentIndex}.address`] ? 'error' : ''}
                          placeholder="Enter full address"
                          disabled={parent.useExisting || (studentForm.useParentAddress && studentForm.selectedParentAddressIndex === parentIndex.toString())}
                        />
                        {formErrors[`parents.${parentIndex}.address`] && (
                          <span className="error-text">{formErrors[`parents.${parentIndex}.address`]}</span>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                          <input
                            type="checkbox"
                            checked={studentForm.useParentAddress && studentForm.selectedParentAddressIndex === parentIndex.toString()}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // When checking, use student address for this parent
                                setStudentForm(prev => {
                                  const updatedParents = [...prev.parents];
                                  updatedParents[parentIndex] = {
                                    ...updatedParents[parentIndex],
                                    address: prev.address || ''
                                  };
                                  return {
                                    ...prev,
                                    useParentAddress: true,
                                    selectedParentAddressIndex: parentIndex.toString(),
                                    parents: updatedParents
                                  };
                                });
                              } else {
                                // When unchecking, clear parent address
                                setStudentForm(prev => {
                                  const updatedParents = [...prev.parents];
                                  updatedParents[parentIndex] = {
                                    ...updatedParents[parentIndex],
                                    address: ''
                                  };
                                  return {
                                    ...prev,
                                    useParentAddress: false,
                                    selectedParentAddressIndex: '',
                                    parents: updatedParents
                                  };
                                });
                              }
                            }}
                            disabled={parent.useExisting || !studentForm.address.trim()}
                            style={{
                              width: '18px',
                              height: '18px',
                              cursor: (parent.useExisting || !studentForm.address.trim()) ? 'not-allowed' : 'pointer',
                              accentColor: '#FF8C42',
                              flexShrink: 0
                            }}
                          />
                          <label style={{ 
                            fontSize: '0.9rem', 
                            color: '#374151',
                            fontWeight: '400',
                            cursor: (parent.useExisting || !studentForm.address.trim()) ? 'not-allowed' : 'pointer',
                            margin: 0
                          }}>
                            Use same address as student
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group" style={{ width: '100%' }}>
                        <label style={{ marginBottom: '0.75rem', display: 'block', fontWeight: '500' }}>
                          Permissions
                        </label>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                          gap: '0.75rem',
                          padding: '1rem',
                          background: '#FFFFFF',
                          border: '1px solid #E5E7EB',
                          borderRadius: '12px',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                        }}>
                          {[
                            { field: 'canPickup', label: 'Can pick from school' },
                            { field: 'isEmergencyContact', label: 'Emergency contact' },
                            { field: 'paysFees', label: 'Pays fees' }
                          ].map((permission) => {
                            const isChecked = parent[permission.field];
                            return (
                              <div
                                key={permission.field}
                                onClick={() => {
                                  if (!parent.useExisting) {
                                    handleParentInputChange(parentIndex, permission.field, !isChecked);
                                  }
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '1rem',
                                  cursor: parent.useExisting ? 'not-allowed' : 'pointer',
                                  borderRadius: '10px',
                                  background: isChecked ? '#FF8C42' : '#FFFFFF',
                                  border: isChecked ? '2px solid #E67E22' : '1px solid #E5E7EB',
                                  transition: 'all 0.2s ease',
                                  opacity: parent.useExisting ? 0.6 : 1,
                                  position: 'relative'
                                }}
                                onMouseEnter={(e) => {
                                  if (!parent.useExisting && !isChecked) {
                                    e.currentTarget.style.background = '#FFF4ED';
                                    e.currentTarget.style.borderColor = '#FFD4B3';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!parent.useExisting && !isChecked) {
                                    e.currentTarget.style.background = '#FFFFFF';
                                    e.currentTarget.style.borderColor = '#E5E7EB';
                                  }
                                }}
                              >
                                <div style={{
                                  width: '24px',
                                  height: '24px',
                                  minWidth: '24px',
                                  marginRight: '0.75rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: isChecked ? '2px solid #FFFFFF' : '2px solid #D1D5DB',
                                  background: isChecked ? '#FFFFFF' : '#FFFFFF',
                                  transition: 'all 0.2s ease'
                                }}>
                                  {isChecked && (
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M11.6667 3.5L5.25 9.91667L2.33334 7" stroke="#FF8C42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  )}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ 
                                    fontWeight: '500',
                                    fontSize: '0.95rem',
                                    color: isChecked ? '#FFFFFF' : '#1F2937'
                                  }}>
                                    {permission.label}
                                  </div>
                                </div>
                                {isChecked && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '0.5rem',
                                    right: '0.5rem',
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    background: '#FFFFFF'
                                  }} />
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {parent.useExisting && (
                          <small style={{ 
                            color: '#6B7280', 
                            fontSize: '12px', 
                            marginTop: '0.5rem', 
                            display: 'block' 
                          }}>
                            Permissions cannot be changed when using an existing parent
                          </small>
                        )}
                      </div>
                    </div>

                    {/* Parent Documents Section */}
                    <div className="form-row" style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
                      <div className="form-group" style={{ width: '100%' }}>
                        <label style={{ marginBottom: '0.75rem', display: 'block', fontWeight: '500', color: '#1F2937' }}>
                          Parent Documents
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          {/* Parent National ID */}
                          <div>
                            <label htmlFor={`parentNationalId-${parentIndex}`} style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500', color: '#374151' }}>
                              Parent National ID
                            </label>
                            <input
                              type="file"
                              id={`parentNationalId-${parentIndex}`}
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  handleParentDocumentAdd(parentIndex, 'Parent National ID', file);
                                  e.target.value = '';
                                }
                              }}
                              disabled={parent.useExisting}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #D1D5DB',
                                borderRadius: '6px',
                                fontSize: '0.9rem',
                                cursor: parent.useExisting ? 'not-allowed' : 'pointer'
                              }}
                            />
                            {parent.documents && parent.documents.find(doc => doc.type === 'Parent National ID') && (
                              <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#059669', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span>✓ {parent.documents.find(doc => doc.type === 'Parent National ID').fileName || 'Parent National ID uploaded'}</span>
                                {!parent.useExisting && (
                                  <button
                                    type="button"
                                    onClick={() => handleParentDocumentRemove(parentIndex, 'Parent National ID')}
                                    style={{
                                      padding: '0.25rem 0.5rem',
                                      backgroundColor: '#EF4444',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Parent Passport */}
                          <div>
                            <label htmlFor={`parentPassport-${parentIndex}`} style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500', color: '#374151' }}>
                              Parent Passport
                            </label>
                            <input
                              type="file"
                              id={`parentPassport-${parentIndex}`}
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  handleParentDocumentAdd(parentIndex, 'Parent Passport', file);
                                  e.target.value = '';
                                }
                              }}
                              disabled={parent.useExisting}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #D1D5DB',
                                borderRadius: '6px',
                                fontSize: '0.9rem',
                                cursor: parent.useExisting ? 'not-allowed' : 'pointer'
                              }}
                            />
                            {parent.documents && parent.documents.find(doc => doc.type === 'Parent Passport') && (
                              <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#059669', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span>✓ {parent.documents.find(doc => doc.type === 'Parent Passport').fileName || 'Parent Passport uploaded'}</span>
                                {!parent.useExisting && (
                                  <button
                                    type="button"
                                    onClick={() => handleParentDocumentRemove(parentIndex, 'Parent Passport')}
                                    style={{
                                      padding: '0.25rem 0.5rem',
                                      backgroundColor: '#EF4444',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        {parent.useExisting && (
                          <small style={{ color: '#6B7280', fontSize: '12px', marginTop: '0.5rem', display: 'block' }}>
                            Documents cannot be changed when using an existing parent
                          </small>
                        )}
                      </div>
                    </div>
                      </div>
                    ))}
                  </div>

                  {/* Add Another Parent Button - Below Parent Section */}
                  <div style={{ marginTop: '1rem', marginBottom: '2rem', display: 'flex', justifyContent: 'flex-start' }}>
                    <button
                      type="button"
                      onClick={addParent}
                      style={{
                        padding: '0.4rem 0.8rem',
                        backgroundColor: '#FF8C42',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        fontSize: '0.85rem',
                        width: 'fit-content',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      + Add Another Parent
                    </button>
                  </div>

                  {/* Emergency Contact Section */}
                  <div className="form-section">
                    <h3>C) Emergency Contact Information</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="emergencyContact">Emergency Contact *</label>
                        <input
                          type="text"
                          id="emergencyContact"
                          name="emergencyContact"
                          value={studentForm.emergencyContact}
                          onChange={handleInputChange}
                          className={formErrors.emergencyContact ? 'error' : ''}
                          placeholder="Emergency contact name"
                        />
                        {formErrors.emergencyContact && (
                          <span className="error-text">{formErrors.emergencyContact}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="emergencyPhone">Emergency Phone *</label>
                        <div className="phone-input-wrapper">
                          <select
                            className="country-code-select"
                            value={receptionistEmergencyPhoneCountryCode}
                            onChange={(e) => setReceptionistEmergencyPhoneCountryCode(e.target.value)}
                          >
                            <option value="+1">🇺🇸 +1 (US/CA)</option>
                            <option value="+44">🇬🇧 +44 (UK)</option>
                            <option value="+91">🇮🇳 +91 (India)</option>
                            <option value="+92">🇵🇰 +92 (Pakistan)</option>
                            <option value="+971">🇦🇪 +971 (UAE)</option>
                            <option value="+966">🇸🇦 +966 (Saudi Arabia)</option>
                            <option value="+974">🇶🇦 +974 (Qatar)</option>
                            <option value="+965">🇰🇼 +965 (Kuwait)</option>
                            <option value="+973">🇧🇭 +973 (Bahrain)</option>
                            <option value="+968">🇴🇲 +968 (Oman)</option>
                            <option value="+961">🇱🇧 +961 (Lebanon)</option>
                            <option value="+962">🇯🇴 +962 (Jordan)</option>
                            <option value="+20">🇪🇬 +20 (Egypt)</option>
                            <option value="+27">🇿🇦 +27 (South Africa)</option>
                            <option value="+61">🇦🇺 +61 (Australia)</option>
                            <option value="+64">🇳🇿 +64 (New Zealand)</option>
                            <option value="+86">🇨🇳 +86 (China)</option>
                            <option value="+81">🇯🇵 +81 (Japan)</option>
                            <option value="+82">🇰🇷 +82 (South Korea)</option>
                            <option value="+65">🇸🇬 +65 (Singapore)</option>
                            <option value="+60">🇲🇾 +60 (Malaysia)</option>
                            <option value="+66">🇹🇭 +66 (Thailand)</option>
                            <option value="+62">🇮🇩 +62 (Indonesia)</option>
                            <option value="+63">🇵🇭 +63 (Philippines)</option>
                            <option value="+84">🇻🇳 +84 (Vietnam)</option>
                            <option value="+880">🇧🇩 +880 (Bangladesh)</option>
                            <option value="+94">🇱🇰 +94 (Sri Lanka)</option>
                            <option value="+977">🇳🇵 +977 (Nepal)</option>
                            <option value="+33">🇫🇷 +33 (France)</option>
                            <option value="+49">🇩🇪 +49 (Germany)</option>
                            <option value="+39">🇮🇹 +39 (Italy)</option>
                            <option value="+34">🇪🇸 +34 (Spain)</option>
                            <option value="+31">🇳🇱 +31 (Netherlands)</option>
                            <option value="+32">🇧🇪 +32 (Belgium)</option>
                            <option value="+41">🇨🇭 +41 (Switzerland)</option>
                            <option value="+43">🇦🇹 +43 (Austria)</option>
                            <option value="+46">🇸🇪 +46 (Sweden)</option>
                            <option value="+47">🇳🇴 +47 (Norway)</option>
                            <option value="+45">🇩🇰 +45 (Denmark)</option>
                            <option value="+358">🇫🇮 +358 (Finland)</option>
                            <option value="+7">🇷🇺 +7 (Russia)</option>
                            <option value="+90">🇹🇷 +90 (Turkey)</option>
                            <option value="+52">🇲🇽 +52 (Mexico)</option>
                            <option value="+55">🇧🇷 +55 (Brazil)</option>
                            <option value="+54">🇦🇷 +54 (Argentina)</option>
                            <option value="+57">🇨🇴 +57 (Colombia)</option>
                            <option value="+234">🇳🇬 +234 (Nigeria)</option>
                            <option value="+254">🇰🇪 +254 (Kenya)</option>
                            <option value="+212">🇲🇦 +212 (Morocco)</option>
                            <option value="+972">🇮🇱 +972 (Israel)</option>
                            <option value="+98">🇮🇷 +98 (Iran)</option>
                            <option value="+964">🇮🇶 +964 (Iraq)</option>
                            <option value="+967">🇾🇪 +967 (Yemen)</option>
                            <option value="+963">🇸🇾 +963 (Syria)</option>
                          </select>
                          <input
                            type="tel"
                            id="emergencyPhone"
                            name="emergencyPhone"
                            value={studentForm.emergencyPhone}
                            onChange={handleInputChange}
                            className={formErrors.emergencyPhone ? 'error' : ''}
                            placeholder="e.g. 300 1234567"
                          />
                        </div>
                        {formErrors.emergencyPhone && (
                          <span className="error-text">{formErrors.emergencyPhone}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Siblings Section */}
                  <div className="form-section">
                    <h3>D) SIBLINGS ATTENDING THE SAME SCHOOL</h3>
                    <div className="siblings-list">
                      {loadingSiblings ? (
                        <p className="loading-text">Loading siblings...</p>
                      ) : siblings.length > 0 ? (
                        <div className="siblings-grid">
                          {siblings.map((sibling) => (
                            <div key={sibling.id} className="sibling-card">
                              <div className="sibling-header">
                                <h5>{sibling.first_name} {sibling.middle_name ? sibling.middle_name + ' ' : ''}{sibling.last_name}</h5>
                              </div>
                              <div className="sibling-details">
                                <p><strong>Email:</strong> {sibling.email || 'N/A'}</p>
                                <p><strong>Class:</strong> {sibling.classes?.class_name || 'N/A'}</p>
                                <p><strong>Section:</strong> {sibling.section_id || 'N/A'}</p>
                                <p><strong>Date of Birth:</strong> {sibling.date_of_birth ? new Date(sibling.date_of_birth).toLocaleDateString() : 'N/A'}</p>
                                <p><strong>Gender:</strong> {sibling.gender || 'N/A'}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-siblings-text">No siblings found</p>
                      )}
                    </div>
                  </div>

                  {/* Teachers Section */}
                  <div className="form-section">
                    <h3>D) TEACHER DETAILS</h3>
                    <div className="teachers-list">
                      {!studentForm.classId || !studentForm.sectionId ? (
                        <p className="no-teachers-text">Please select a class and section to view assigned teachers.</p>
                      ) : loadingTeachers ? (
                        <p className="loading-text">Loading teachers...</p>
                      ) : teachers.length > 0 ? (
                        <div className="teachers-grid">
                          {teachers.map((teacher) => (
                            <div key={teacher.id} className="teacher-card">
                              <div className="teacher-header">
                                <h5>{teacher.first_name} {teacher.last_name}</h5>
                              </div>
                              <div className="teacher-details">
                                <p><strong>Email:</strong> {teacher.email || 'N/A'}</p>
                                <p><strong>Phone:</strong> {teacher.phone || 'N/A'}</p>
                                <p><strong>Qualification:</strong> {teacher.qualification || 'N/A'}</p>
                                {teacher.subjects && Array.isArray(teacher.subjects) && teacher.subjects.length > 0 && (
                                  <p><strong>Subjects:</strong> {teacher.subjects.join(', ')}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-teachers-text">No teachers assigned to this class and section.</p>
                      )}
                    </div>
                  </div>

                  {/* Medical Details Section */}
                  <div className="form-section">
                    <h3>E) MEDICAL RECORDS</h3>
                    
                    <div className="form-row">
                      <div className="form-group full-width">
                        <label htmlFor="medicalDetails">Medical Records</label>
                        <div className="medical-records-guidelines">
                          <p className="medical-guideline-text">Please provide the following information:</p>
                          <ol className="medical-guidelines-list">
                            <li>Any known allergies or medical conditions of the student</li>
                            <li>Information on any required medications or treatments</li>
                            <li>Emergency documents</li>
                          </ol>
                        </div>
                        <textarea
                          id="medicalDetails"
                          name="medicalDetails"
                          value={studentForm.medicalDetails}
                          onChange={handleInputChange}
                          placeholder="Enter medical records here..."
                          rows="10"
                          style={{ minHeight: '200px' }}
                        />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group full-width">
                        <label htmlFor="emergencyDocuments">Emergency Documents (PDF, DOC, DOCX, JPG, PNG)</label>
                        <input
                          type="file"
                          id="emergencyDocuments"
                          name="emergencyDocuments"
                          onChange={handleEmergencyDocumentsChange}
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          className={formErrors.emergencyDocuments ? 'error' : ''}
                        />
                        {formErrors.emergencyDocuments && (
                          <span className="error-text">{formErrors.emergencyDocuments}</span>
                        )}
                        {studentForm.emergencyDocuments && (
                          <div className="file-selected">
                            <span>Selected: {studentForm.emergencyDocuments.name}</span>
                            <span className="file-size">({(studentForm.emergencyDocuments.size / 1024 / 1024).toFixed(2)} MB)</span>
                          </div>
                        )}
                        <small className="form-help">
                          Upload emergency medical documents (Max 10MB)
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Academic History Section */}
                  <div className="form-section">
                    <h3>F) ACADEMIC HISTORY</h3>
                    <div className="academic-history-content">
                      <div className="academic-history-item">
                        <p className="academic-history-label">1. Previous school(s) attended:</p>
                        <p className="academic-history-value">
                          {studentForm.previousSchool ? studentForm.previousSchool : 'Not specified'}
                        </p>
                      </div>
                      {studentForm.previousSchoolGrade && (
                        <div className="academic-history-item">
                          <p className="academic-history-label">Grade/Percentage from Previous School:</p>
                          <p className="academic-history-value">
                            {studentForm.previousSchoolGrade}
                          </p>
                        </div>
                      )}
                      <div className="academic-history-item">
                        <p className="academic-history-label">2. Grade/Class Assigned:</p>
                        <p className="academic-history-value">
                          {studentForm.classId ? (
                            <>
                              {classes.find(c => c.id === studentForm.classId)?.class_name || 'Class ' + studentForm.classId}
                              {studentForm.sectionId && ` - Section ${studentForm.sectionId}`}
                            </>
                          ) : 'Not assigned'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Transport Section */}
                  <div className="form-section">
                    <h3>Transport Service</h3>
                    
                    <div className="transport-service-toggle-container">
                      <label className="transport-service-label">
                        <span>Enable Transport Service</span>
                        <div className="toggle-switch">
                          <input
                            type="checkbox"
                            id="transportServiceEnabled"
                            name="transportServiceEnabled"
                            checked={studentForm.transportServiceEnabled}
                            onChange={handleInputChange}
                          />
                          <span className="toggle-slider"></span>
                        </div>
                      </label>
                      <p className="transport-service-help">
                        {studentForm.transportServiceEnabled 
                          ? 'Transport service is enabled for this student.' 
                          : 'Transport service is disabled for this student.'}
                      </p>
                      
                      {studentForm.transportServiceEnabled && (
                        <div className="form-group" style={{ marginTop: '1.5rem' }}>
                          <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                id="useStudentAddressAsPickup"
                                name="useStudentAddressAsPickup"
                                checked={studentForm.useStudentAddressAsPickup}
                                onChange={(e) => {
                                  setStudentForm(prev => ({
                                    ...prev,
                                    useStudentAddressAsPickup: e.target.checked,
                                    pickupLocationId: e.target.checked ? '' : prev.pickupLocationId, // Clear pickup location if using address
                                    // Don't clear route type - user still needs to select it
                                    routeId: e.target.checked ? '' : prev.routeId // Clear route
                                  }));
                                }}
                                style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#FF8C42' }}
                              />
                              <span style={{ fontSize: '0.95rem', fontWeight: '500', color: '#374151' }}>
                                Use Student Address as Pickup Location
                              </span>
                            </label>
                          </div>
                          
                          {/* Route Type Selection */}
                          <div className="form-group" style={{ marginTop: '1rem' }}>
                            <label htmlFor="transportRouteType" style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.95rem', fontWeight: '600', color: '#374151' }}>
                              Select Route Type (Optional)
                            </label>
                            <select
                              id="transportRouteType"
                              name="transportRouteType"
                              value={studentForm.transportRouteType}
                              onChange={(e) => {
                                handleInputChange(e);
                                // Reset transport time and route when route type changes
                                setStudentForm(prev => ({
                                  ...prev,
                                  transportRouteType: e.target.value,
                                  transportTime: e.target.value !== '1 Way' ? '' : prev.transportTime,
                                  routeId: ''
                                }));
                              }}
                              className={formErrors.transportRouteType ? 'error' : ''}
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: formErrors.transportRouteType ? '2px solid #EF4444' : '1px solid #D1D5DB',
                                borderRadius: '8px',
                                fontSize: '0.95rem',
                                backgroundColor: '#FFFFFF',
                                cursor: 'pointer'
                              }}
                            >
                              <option value="">-- Select route type (Optional) --</option>
                              <option value="1 Way">1 Way</option>
                              <option value="2 Way">2 Way</option>
                            </select>
                            {formErrors.transportRouteType && (
                              <span className="error-text" style={{ display: 'block', marginTop: '0.5rem', color: '#EF4444', fontSize: '0.875rem' }}>
                                {formErrors.transportRouteType}
                              </span>
                            )}
                          </div>
                          
                          {/* Morning/Evening Selection for 1 Way */}
                          {studentForm.transportRouteType === '1 Way' && (
                            <div className="form-group" style={{ marginTop: '1rem' }}>
                              <label htmlFor="transportTime" style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.95rem', fontWeight: '600', color: '#374151' }}>
                                Select Time (Optional)
                              </label>
                              <select
                                id="transportTime"
                                name="transportTime"
                                value={studentForm.transportTime}
                                onChange={handleInputChange}
                                className={formErrors.transportTime ? 'error' : ''}
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: formErrors.transportTime ? '2px solid #EF4444' : '1px solid #D1D5DB',
                                  borderRadius: '8px',
                                  fontSize: '0.95rem',
                                  backgroundColor: '#FFFFFF',
                                  cursor: 'pointer'
                                }}
                              >
                                <option value="">-- Select time (Optional) --</option>
                                <option value="Morning">Morning</option>
                                <option value="Evening">Evening</option>
                              </select>
                              {formErrors.transportTime && (
                                <span className="error-text" style={{ display: 'block', marginTop: '0.5rem', color: '#EF4444', fontSize: '0.875rem' }}>
                                  {formErrors.transportTime}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Fees Information Section */}
                  <div className="form-section">
                    <h3>Fees Information</h3>
                    <div className="fees-content">
                      {!studentForm.classId ? (
                        <p className="fees-placeholder">Please select a class to view fees information.</p>
                      ) : loadingFees ? (
                        <p className="loading-text">Loading fees...</p>
                      ) : classFees && classFees.length > 0 ? (
                        <div className="fees-list">
                          <div className="fees-header">
                            <h4>Fees for Selected Class</h4>
                          </div>
                          {classFees.map((fee) => {
                            // Calculate transportation fee if enabled
                            const transportFee = studentForm.transportServiceEnabled && studentForm.pickupLocationId && studentForm.transportRouteType
                              ? (() => {
                                  const selectedLocation = pickupLocations.find(loc => loc.id.toString() === studentForm.pickupLocationId);
                                  if (selectedLocation) {
                                    if (studentForm.transportRouteType === '1 Way' && selectedLocation.price_one_way) {
                                      return parseFloat(selectedLocation.price_one_way);
                                    } else if (studentForm.transportRouteType === '2 Way' && selectedLocation.price_two_way) {
                                      return parseFloat(selectedLocation.price_two_way);
                                    }
                                  }
                                  return 0;
                                })()
                              : 0;

                            // Calculate monthly breakdown
                            const baseAmount = parseFloat(fee.amount);
                            let monthlyTuition = 0;
                            let feeBreakdown = [];

                            switch (fee.fee_type) {
                              case 'monthly':
                                monthlyTuition = baseAmount;
                                feeBreakdown.push({
                                  label: 'Monthly Tuition',
                                  amount: baseAmount,
                                  period: 'monthly'
                                });
                                break;
                              case 'quarterly':
                                monthlyTuition = baseAmount / 3;
                                feeBreakdown.push({
                                  label: 'Quarterly Tuition',
                                  amount: baseAmount,
                                  period: 'quarterly'
                                });
                                feeBreakdown.push({
                                  label: 'Monthly Portion (Quarterly ÷ 3)',
                                  amount: monthlyTuition,
                                  period: 'monthly'
                                });
                                break;
                              case 'semester':
                                monthlyTuition = baseAmount / 6;
                                feeBreakdown.push({
                                  label: 'Semester Tuition',
                                  amount: baseAmount,
                                  period: 'semester'
                                });
                                feeBreakdown.push({
                                  label: 'Monthly Portion (Semester ÷ 6)',
                                  amount: monthlyTuition,
                                  period: 'monthly'
                                });
                                break;
                              case 'annual':
                                monthlyTuition = baseAmount / 12;
                                feeBreakdown.push({
                                  label: 'Annual Tuition',
                                  amount: baseAmount,
                                  period: 'annual'
                                });
                                feeBreakdown.push({
                                  label: 'Monthly Portion (Annual ÷ 12)',
                                  amount: monthlyTuition,
                                  period: 'monthly'
                                });
                                break;
                            }

                            // Calculate additional fees
                            const monthlyAdditionalFees = additionalFees
                              .filter(af => af.period === 'monthly')
                              .reduce((sum, af) => sum + (parseFloat(af.amount) || 0), 0);
                            
                            const oneTimeAdditionalFees = additionalFees
                              .filter(af => af.period === 'one_time')
                              .reduce((sum, af) => sum + (parseFloat(af.amount) || 0), 0);

                            const totalMonthlyFee = monthlyTuition + transportFee + monthlyAdditionalFees;

                            return (
                              <div key={fee.id} className="fee-item" style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #E5E7EB', borderRadius: '8px', backgroundColor: '#FAFAFA' }}>
                              <div className="fee-details">
                                  {/* Monthly Fee Header */}
                                  <div style={{ marginBottom: '1rem' }}>
                                    <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', fontWeight: '600', color: '#1F2937' }}>
                                      MONTHLY FEE:
                                    </p>
                                    <div style={{ 
                                      backgroundColor: '#FFFFFF', 
                                      borderRadius: '8px', 
                                      padding: '0.75rem 1rem',
                                      textAlign: 'center',
                                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                    }}>
                                      <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: '#1F2937' }}>
                                        {fee.currency || 'PKR'} {totalMonthlyFee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </p>
                                    </div>
                                  </div>
                                  
                                  {/* Fee Breakdown */}
                                  <div style={{ 
                                    backgroundColor: '#FFFFFF', 
                                    borderRadius: '8px', 
                                    padding: '1rem',
                                    borderLeft: '3px solid #E67E22',
                                    borderBottom: '3px solid #E67E22',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                  }}>
                                    <p style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
                                      Monthly Breakdown:
                                    </p>
                                    {/* Monthly Tuition */}
                                    {feeBreakdown.map((breakdown, idx) => (
                                      <div key={idx} style={{ 
                                        backgroundColor: '#FFFFFF', 
                                        border: '1px solid #E5E7EB',
                                        borderRadius: '6px',
                                        padding: '0.75rem 1rem',
                                        marginBottom: '0.5rem',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                      }}>
                                        <span style={{ fontSize: '0.9rem', color: '#1F2937' }}>{breakdown.label}:</span>
                                        <span style={{ fontSize: '0.9rem', fontWeight: '500', color: '#6B7280' }}>{fee.currency || 'PKR'} {breakdown.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                      </div>
                                    ))}
                                    
                                    {/* Additional Monthly Fees */}
                                    {monthlyAdditionalFees > 0 && additionalFees.filter(af => af.period === 'monthly').map((af, idx) => (
                                      <div key={`monthly-${idx}`} style={{ 
                                        backgroundColor: '#FFFFFF', 
                                        border: '1px solid #E5E7EB',
                                        borderRadius: '6px',
                                        padding: '0.75rem 1rem',
                                        marginBottom: '0.5rem',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                      }}>
                                        <span style={{ fontSize: '0.9rem', color: '#1F2937' }}>{af.fee_name || 'Monthly Additional Fee'}:</span>
                                        <span style={{ fontSize: '0.9rem', fontWeight: '500', color: '#6B7280' }}>{fee.currency || 'PKR'} {parseFloat(af.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                      </div>
                                    ))}
                                    
                                    {/* One-Time Additional Fees (shown only in first payment) */}
                                    {oneTimeAdditionalFees > 0 && additionalFees.filter(af => af.period === 'one_time').map((af, idx) => (
                                      <div key={`onetime-${idx}`} style={{ 
                                        backgroundColor: '#FFFFFF', 
                                        border: '1px solid #E5E7EB',
                                        borderRadius: '6px',
                                        padding: '0.75rem 1rem',
                                        marginBottom: '0.5rem',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                      }}>
                                        <span style={{ fontSize: '0.9rem', color: '#1F2937' }}>{af.fee_name || 'One-Time Fee'}:</span>
                                        <span style={{ fontSize: '0.9rem', fontWeight: '500', color: '#6B7280' }}>{fee.currency || 'PKR'} {parseFloat(af.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                      </div>
                                    ))}
                                    
                                    {/* Transportation Fee */}
                                    {transportFee > 0 && (
                                      <div style={{ 
                                        backgroundColor: '#FFFFFF', 
                                        border: '1px solid #E5E7EB',
                                        borderRadius: '6px',
                                        padding: '0.75rem 1rem',
                                        marginBottom: '0.5rem',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                      }}>
                                        <span style={{ fontSize: '0.9rem', color: '#1F2937' }}>Transportation (Monthly):</span>
                                        <span style={{ fontSize: '0.9rem', fontWeight: '500', color: '#6B7280' }}>
                                          {fee.currency || 'PKR'} {transportFee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                      </div>
                                    )}
                                    
                                    {/* Total Monthly Fee */}
                                    <div style={{ 
                                      backgroundColor: '#FFFFFF', 
                                      border: '2px solid #10B981',
                                      borderRadius: '6px',
                                      padding: '0.75rem 1rem',
                                      marginTop: '0.75rem',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      borderTop: '2px solid #10B981'
                                    }}>
                                      <span style={{ fontSize: '1rem', fontWeight: '700', color: '#1F2937' }}>Total Monthly Fee:</span>
                                      <span style={{ fontSize: '1rem', fontWeight: '700', color: '#1F2937' }}>
                                        {fee.currency || 'PKR'} {totalMonthlyFee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </span>
                                    </div>
                                  </div>

                                {fee.description && (
                                    <p className="fee-description" style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6B7280' }}>{fee.description}</p>
                                )}
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="no-fees-text">No fees set for this class yet.</p>
                      )}
                      
                      <div className="payment-method-section">
                        <label className="payment-method-label">Payment Method *</label>
                        <div className="payment-method-options">
                          <label className="payment-option">
                            <input
                              type="radio"
                              name="paymentMethod"
                              value="Cash"
                              checked={studentForm.paymentMethod === 'Cash'}
                              onChange={handleInputChange}
                              className={formErrors.paymentMethod ? 'error' : ''}
                            />
                            <span>a. Cash</span>
                          </label>
                          <label className="payment-option">
                            <input
                              type="radio"
                              name="paymentMethod"
                              value="Cheque"
                              checked={studentForm.paymentMethod === 'Cheque'}
                              onChange={handleInputChange}
                              className={formErrors.paymentMethod ? 'error' : ''}
                            />
                            <span>b. Cheque</span>
                          </label>
                          <label className="payment-option">
                            <input
                              type="radio"
                              name="paymentMethod"
                              value="Mobile money"
                              checked={studentForm.paymentMethod === 'Mobile money'}
                              onChange={handleInputChange}
                              className={formErrors.paymentMethod ? 'error' : ''}
                            />
                            <span>c. Any other (Mobile money)</span>
                          </label>
                        </div>
                        {formErrors.paymentMethod && (
                          <span className="error-text">{formErrors.paymentMethod}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Documents Section */}
                  <div className="form-section">
                    <h3>I) DOCUMENTS</h3>
                    <div className="documents-content">
                      <div className="document-upload-section">
                        <div className="document-upload-controls">
                          <label htmlFor="documentTypeSelect" className="document-type-label">Document Type</label>
                          <select
                            id="documentTypeSelect"
                            className="document-type-select"
                            defaultValue=""
                          >
                            <option value="">Select Document Type</option>
                            <option value="Birth Certificate">Birth Certificate</option>
                            <option value="Academic Transcripts">Academic Transcripts</option>
                            <option value="Previous School Certificate">Previous School Certificate</option>
                            <option value="Medical Certificate">Medical Certificate</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <input
                          type="file"
                          id="documentFileInput"
                          className="document-file-input"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            const typeSelect = document.getElementById('documentTypeSelect');
                            const type = typeSelect.value;
                            if (file && type) {
                              handleDocumentAdd(type, file);
                              e.target.value = ''; // Reset input
                              typeSelect.value = ''; // Reset select
                            } else if (file && !type) {
                              alert('Please select a document type first');
                              e.target.value = '';
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="add-document-btn"
                          onClick={() => {
                            const typeSelect = document.getElementById('documentTypeSelect');
                            const type = typeSelect.value;
                            if (!type) {
                              alert('Please select a document type first');
                              return;
                            }
                            document.getElementById('documentFileInput').click();
                          }}
                        >
                          + Add Document
                        </button>
                      </div>
                      
                      {studentForm.documents && studentForm.documents.length > 0 && (
                        <div className="documents-list">
                          <h4>Uploaded Documents ({studentForm.documents.length})</h4>
                          {studentForm.documents.map((doc, index) => (
                            <div key={index} className="document-item">
                              <div className="document-info">
                                <select
                                  value={doc.type}
                                  onChange={(e) => handleDocumentTypeChange(index, e.target.value)}
                                  className="document-type-edit"
                                  disabled={doc.existingDocument} // Disable type change for existing parent documents
                                >
                                  <option value="Birth Certificate">Birth Certificate</option>
                                  <option value="Academic Transcripts">Academic Transcripts</option>
                                  <option value="Previous School Certificate">Previous School Certificate</option>
                                  <option value="Medical Certificate">Medical Certificate</option>
                                  <option value="Other">Other</option>
                                </select>
                                {doc.existingDocument ? (
                                  <>
                                    <a 
                                      href={doc.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="document-name"
                                      style={{ color: '#FF8C42', textDecoration: 'none', marginLeft: '0.5rem' }}
                                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                    >
                                      {doc.fileName || doc.type} (from existing parent) →
                                    </a>
                                    {doc.fileSize && (
                                      <span className="document-size">({(doc.fileSize / 1024 / 1024).toFixed(2)} MB)</span>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <span className="document-name">{doc.file?.name || doc.fileName || 'Document'}</span>
                                    <span className="document-size">({doc.file ? (doc.file.size / 1024 / 1024).toFixed(2) : doc.fileSize ? (doc.fileSize / 1024 / 1024).toFixed(2) : '0.00'} MB)</span>
                                  </>
                                )}
                              </div>
                              <button
                                type="button"
                                className="remove-document-btn"
                                onClick={() => handleDocumentRemove(index)}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {(!studentForm.documents || studentForm.documents.length === 0) && (
                        <p className="no-documents-text">No documents uploaded yet.</p>
                      )}
                    </div>
                  </div>

                  <div className="form-actions">
                    <button 
                      type="submit" 
                      className="submit-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Adding Student...' : 'Add Student'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

      {/* Beautiful Success Popup */}
      {showSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowSuccessPopup(false)}
        >
          <div 
            className="success-popup"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              color: 'white',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              zIndex: 0
            }}></div>

            {/* Success Icon */}
            <div style={{
              fontSize: '80px',
              marginBottom: '1.5rem',
              animation: 'bounceIn 0.6s ease-out',
              position: 'relative',
              zIndex: 1
            }}>
              ✅
            </div>

            {/* Success Message */}
            <h2 style={{
              fontSize: '2rem',
              fontWeight: '700',
              margin: '0 0 1rem 0',
              color: 'white',
              position: 'relative',
              zIndex: 1
            }}>
              Student Added Successfully!
            </h2>

            <p style={{
              fontSize: '1.1rem',
              margin: '0 0 1.5rem 0',
              color: 'rgba(255, 255, 255, 0.95)',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              <strong style={{ fontSize: '1.2rem' }}>{addedStudentName}</strong> has been successfully added to the system.
            </p>

            <div style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '2rem',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              position: 'relative',
              zIndex: 1
            }}>
              <p style={{
                margin: '0',
                fontSize: '0.95rem',
                color: 'rgba(255, 255, 255, 0.9)',
                lineHeight: '1.6'
              }}>
                📋 <strong>Status:</strong> Pending School Approval<br/>
                The student will appear in the system once approved by the school administrator.
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={() => {
                setShowSuccessPopup(false);
                // Optionally reset the form or navigate
              }}
              style={{
                background: 'white',
                color: '#667eea',
                border: 'none',
                borderRadius: '12px',
                padding: '1rem 2.5rem',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                position: 'relative',
                zIndex: 1
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
              }}
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {/* Add CSS animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes slideUp {
          from {
            transform: translateY(50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes bounceIn {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default AddStudentReceptionist;
